import React, { useState, useEffect, useRef, useMemo } from "react";
import { supabase } from "./supabase";
import {
  obtenerPermisosCliente,
  puedeAccederAVista,
  obtenerMensajeRestriccion,
} from "./permisos";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Wallet,
  Building2,
  Users,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  Search,
  Filter,
  Download,
  Upload,
  Calendar,
  DollarSign,
  FileText,
  User,
  Clock,
  MapPin,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  ChevronDown,
  Settings,
  Receipt,
  Package,
  Layers,
  Target,
  Truck,
} from "lucide-react";

// Configuración anti-zoom
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// Colores por vista
const VISTA_COLORS = {
  presupuestos: {
    gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
    solid: "#2563eb",
    light: "#2563eb10",
    description: "Gestión de presupuestos",
  },
};

const getVistaColor = (vistaId) => {
  return VISTA_COLORS[vistaId] || VISTA_COLORS["presupuestos"];
};

// Función para formatear números con separadores (miles con coma, decimales con punto)
const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";

  // Asegurarse de que el valor sea una cadena y eliminar cualquier coma existente (si el input tiene un formato previo)
  let numStr = String(value).replace(/,/g, "");

  // Manejar múltiples puntos decimales (si de alguna manera existen de una entrada incorrecta previa)
  const parts = numStr.split(".");
  if (parts.length > 2) {
    numStr = parts[0] + "." + parts.slice(1).join("");
  }

  // Separar la parte entera de la parte decimal
  const [integerPart, decimalPart] = numStr.split(".");

  // Formatear la parte entera con comas como separadores de miles
  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ","
  );

  // Combinar con la parte decimal si existe, usando un punto como separador decimal
  if (decimalPart !== undefined) {
    return `${formattedIntegerPart}.${decimalPart}`;
  }
  return formattedIntegerPart;
};

// Función para convertir formato display (miles con coma, decimales con punto) a número interno
const parseFormattedNumber = (formattedValue) => {
  if (
    formattedValue === null ||
    formattedValue === undefined ||
    formattedValue === ""
  )
    return "";
  // Eliminar comas (separadores de miles)
  let cleanValue = String(formattedValue).replace(/,/g, "");
  // El punto ya debería ser el separador decimal para el formato interno, así que no se necesita reemplazar más
  return cleanValue;
};

// CustomDropdown unificado con búsqueda
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "14px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onFocus={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#2563eb";
            e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
          }
        }}
        onBlur={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#f1f5f9";
            e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
          }
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "280px",
            overflowY: "auto",
          }}
        >
          {/* Campo de búsqueda */}
          <div style={{ padding: "8px" }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
            />
          </div>

          {/* Opciones filtradas */}
          {filteredOptions.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
                setSearchTerm("");
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.backgroundColor = "#f8fafc";
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.backgroundColor = "transparent";
                }
              }}
            >
              <span>{option.label}</span>
              {option.hasPresupuestoInicial && (
                <Target size={12} color="#2563eb" />
              )}
            </button>
          ))}

          {/* Mensaje cuando no hay resultados */}
          {filteredOptions.length === 0 && (
            <div
              style={{
                padding: "12px 16px",
                color: "#64748b",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              No se encontraron resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Funciones helper para manejar fechas
const formatDateForInput = (dateString) => {
  if (!dateString) return new Date().toISOString().split("T")[0];
  if (dateString.includes("T")) {
    return dateString.split("T")[0];
  }
  return dateString;
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

// Modal personalizado para alertas/confirmaciones
function CustomModal({
  isOpen,
  onClose,
  type = "info",
  title,
  message,
  onConfirm,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  isMobile,
}) {
  if (!isOpen) return null;

  const getColors = () => {
    switch (type) {
      case "confirm":
        return {
          bg: "#e0e7ff",
          border: "#2563eb",
          text: "#2563eb",
          button: "#2563eb",
        };
      case "error":
        return {
          bg: "#fef2f2",
          border: "#ef4444",
          text: "#dc2626",
          button: "#ef4444",
        };
      case "success":
        return {
          bg: "#f0fdf4",
          border: "#22c55e",
          text: "#166534",
          button: "#22c55e",
        };
      default:
        return {
          bg: "#e0e7ff",
          border: "#2563eb",
          text: "#2563eb",
          button: "#2563eb",
        };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: isMobile ? "90%" : "400px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: `2px solid ${colors.border}`,
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              margin: "0 auto 16px",
              borderRadius: "32px",
              background: colors.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {type === "confirm" && (
              <AlertCircle size={32} color={colors.text} />
            )}
            {type === "error" && <XCircle size={32} color={colors.text} />}
            {type === "success" && (
              <CheckCircle2 size={32} color={colors.text} />
            )}
            {type === "info" && <Info size={32} color={colors.text} />}
          </div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: colors.text,
            }}
          >
            {title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#64748b",
              lineHeight: "1.4",
            }}
          >
            {message}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: type === "confirm" ? "space-between" : "center",
          }}
        >
          {type === "confirm" && (
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: "12px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                background: "white",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              className="button-hover"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={() => {
              if (type === "confirm" && onConfirm) {
                onConfirm();
              }
              onClose();
            }}
            style={{
              flex: type === "confirm" ? 1 : "auto",
              padding: "12px 20px",
              border: "none",
              borderRadius: "8px",
              background: colors.button,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              minWidth: type !== "confirm" ? "120px" : "auto",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            className="button-hover"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function Presupuestos({ constructora, usuario }) {
  const [activeTab, setActiveTab] = useState("gastos_generales");
  const [presupuestos, setPresupuestos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [conceptosSalida, setConceptosSalida] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [subetapas, setSubetapas] = useState([]);
  const [presupuestosIniciales, setPresupuestosIniciales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPresupuesto, setEditingPresupuesto] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailPresupuesto, setDetailPresupuesto] = useState(null);
  const [showAdicionalModal, setShowAdicionalModal] = useState(false);
  const [editingAdicional, setEditingAdicional] = useState(null);
  const [adicionales, setAdicionales] = useState([]);
  const [pagosDetectados, setPagosDetectados] = useState([]);
  const [dolarBlue, setDolarBlue] = useState(null);
  const [datosManoObra, setDatosManoObra] = useState([]);
  const [proyectosExpandidos, setProyectosExpandidos] = useState(new Set());
  const [loadingDolar, setLoadingDolar] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [cajas, setCajas] = useState([]);
  const [dolarOficial, setDolarOficial] = useState(null);

  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProject, setFilterProject] = useState(() => {
    // Intentar restaurar el último filtro de proyecto desde localStorage
    try {
      const saved = localStorage.getItem('presupuestos_filterProject');
      return saved || "todos";
    } catch (error) {
      console.error('Error restaurando filtro de proyecto:', error);
      return "todos";
    }
  });
  const [filterEstado, setFilterEstado] = useState("todos");
  const [filterVigencia, setFilterVigencia] = useState("activos");

  // =========================
  // Filtros aplicados a Mano de Obra
  // =========================
  const datosManoObraFiltrados = useMemo(() => {
    try {
      if (!Array.isArray(datosManoObra) || datosManoObra.length === 0) return [];

      // Si hay filtro de proyecto seleccionado, filtrar por proyecto_id
      if (filterProject && filterProject !== "todos") {
        return datosManoObra.filter((d) => d.proyecto_id === filterProject);
      }

      return datosManoObra;
    } catch (e) {
      console.error("Error filtrando datos de mano de obra:", e);
      return datosManoObra || [];
    }
  }, [datosManoObra, filterProject]);
  const [filterEtapa, setFilterEtapa] = useState("todos");

  // Estados para permisos de cliente y alerts
  const [permisosCliente, setPermisosCliente] = useState({ esCliente: null });
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });

  // ESC handler obligatorio
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowModal(false);
        setShowDetailModal(false);
        setShowAdicionalModal(false);
        setCustomModal((prev) => ({ ...prev, isOpen: false }));
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Bloquear scroll del body cuando hay modales abiertos
  useEffect(() => {
    if (showModal || showDetailModal || showAdicionalModal || customModal.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Limpiar al desmontar
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal, showDetailModal, showAdicionalModal, customModal.isOpen]);

  // Scroll reset al cargar
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Cargar permisos de cliente al inicio
  useEffect(() => {
    if (constructora?.id && usuario) {
      cargarPermisosCliente();
    }
  }, [constructora, usuario]);

  useEffect(() => {
    console.log("🔄 useEffect ejecutado - activeTab:", activeTab, "constructora:", constructora?.id, "permisos:", Object.keys(permisosCliente).length);
    
    if (constructora?.id && Object.keys(permisosCliente).length > 0) {
      cargarDatos();
      cargarDolarBlue();
      cargarCajas();
      cargarDolarOficial();
    }
  }, [constructora, permisosCliente, filterVigencia]);

  // useEffect separado para cargar datos de mano de obra cuando cambie la pestaña y los proyectos estén cargados
  useEffect(() => {
    if (activeTab === "mano_obra" && proyectos.length > 0) {
      console.log("🚀 Ejecutando cargarDatosManoObra desde useEffect separado");
      cargarDatosManoObra();
    }
  }, [activeTab, proyectos]);

  // Función para guardar el filtro de proyecto en localStorage
  const guardarFiltroProyecto = (filtro) => {
    try {
      localStorage.setItem('presupuestos_filterProject', filtro);
    } catch (error) {
      console.error('Error guardando filtro de proyecto:', error);
    }
  };

  // Función para mostrar alerts personalizados
  const showCustomAlert = (type, title, message, onConfirm = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
    });
  };

  // Función para cargar permisos de cliente
  const cargarPermisosCliente = async () => {
    try {
      const permisos = await obtenerPermisosCliente(usuario, constructora);
      setPermisosCliente(permisos);

      // Si es cliente, verificar que puede ver presupuestos
      if (
        permisos.esCliente &&
        !puedeAccederAVista(permisos.permisos, "presupuestos")
      ) {
        const mensajeRestriccion = obtenerMensajeRestriccion(
          permisos,
          "presupuestos"
        );
        showCustomAlert("error", "Acceso Denegado", mensajeRestriccion);
        return;
      }
    } catch (error) {
      console.error("Error cargando permisos:", error);
      setPermisosCliente({ esCliente: false });
    }
  };

  // Función para verificar si existe presupuesto inicial
  const verificarPresupuestoInicial = (
    proyectoId,
    conceptoId,
    etapaId,
    subetapaId
  ) => {
    if (!proyectoId || !conceptoId || !etapaId || !subetapaId) return false;

    return presupuestosIniciales.some(
      (p) =>
        p.proyecto_id === proyectoId &&
        p.concepto_salida_id === conceptoId &&
        p.etapa_id === etapaId &&
        p.subetapa_id === subetapaId
    );
  };

  const cargarDatos = async () => {
    try {
      // Filtrar por permisos - si es cliente, solo gastos_generales
      const tipoPresupuestoFiltro = permisosCliente.esCliente
        ? "gastos_generales"
        : activeTab;

      // Query base con filtros de cliente
      let query = supabase
        .from("presupuestos")
        .select(
          `
          *,
          proyectos (
            codigo,
            apellido_cliente
          ),
          proveedores_servicios (
            nombre,
            cuit
          ),
          conceptos_salida (
            nombre
          ),
          etapas_obra (
            nombre
          ),
                            subetapas_obra!left (
                    nombre
                  )
        `
        )
        .eq("constructora_id", constructora.id)
        .eq("tipo_presupuesto", tipoPresupuestoFiltro);

      if (filterVigencia === "activos") {
        query = query.eq("activo", true).is("deleted_at", null);
      } else if (filterVigencia === "eliminados") {
        query = query.or("activo.eq.false,deleted_at.not.is.null");
      }

      query = query.order("created_at", { ascending: false });

      // Si es cliente, filtrar solo su proyecto
      if (permisosCliente.esCliente && permisosCliente.proyectoId) {
        query = query.eq("proyecto_id", permisosCliente.proyectoId);
      }

      const { data: presupuestosData, error: presupuestosError } = await query;

      if (presupuestosError) {
        console.error("Error cargando presupuestos:", presupuestosError);
      } else {
        // Para cada presupuesto, calcular totales y pagos
        const presupuestosConCalculos = await Promise.all(
          (presupuestosData || []).map(async (presupuesto) => {
            try {
              // Calcular total presupuesto
              const { data: totalData } = await supabase.rpc(
                "calcular_total_presupuesto",
                { presupuesto_uuid: presupuesto.id }
              );

              // Calcular saldo presupuesto
              const { data: saldoData } = await supabase.rpc(
                "calcular_saldo_presupuesto",
                { presupuesto_uuid: presupuesto.id }
              );

              // Cargar datos financieros detallados para todos los presupuestos
              let datosFinancieros = null;
              {
                try {
                  // Cargar salidas
                  const { data: salidasData } = await supabase
                    .from("salidas")
                    .select("monto, moneda, tipo_cambio, tiene_comision, monto_comision, comision_retencion_en_origen, comision_cobrada")
                    .eq("constructora_id", presupuesto.constructora_id)
                    .eq("proyecto_id", presupuesto.proyecto_id)
                    .eq("proveedor_id", presupuesto.proveedor_id)
                    .eq("concepto_id", presupuesto.concepto_salida_id)
                    .eq("activo", true)
                    .is("deleted_at", null);

                  // Cargar adicionales
                  const { data: adicionalesData } = await supabase
                    .from("adicionales_presupuesto")
                    .select("monto, monto_real_proveedor, destino_adicional, aplica_cliente, aplica_proveedor")
                    .eq("presupuesto_id", presupuesto.id)
                    .eq("activo", true)
                    .is("deleted_at", null);

                  let pagadoClienteARS = 0;
                  let pagadoProveedorNetoARS = 0;
                  let adicionalesClienteARS = 0;
                  let adicionalesProveedorARS = 0;
                  let comisionTotalARS = 0;
                  let comisionCobradaARS = 0;

                  // Calcular pagos y comisiones
                  if (salidasData) {
                    salidasData.forEach((salida) => {
                      const tc = parseFloat(salida.tipo_cambio || 1);
                      const montoARS = salida.moneda === "USD" ? salida.monto * tc : salida.monto;
                      
                      pagadoClienteARS += montoARS;
                      
                      // Calcular comisiones
                      if (salida.tiene_comision) {
                        const comisionARS = salida.moneda === "USD" ? (salida.monto_comision || 0) * tc : (salida.monto_comision || 0);
                        comisionTotalARS += comisionARS;
                        
                        // Comisión cobrada si está marcada como retenida en origen O cobrada
                        if (salida.comision_retencion_en_origen || salida.comision_cobrada) {
                          comisionCobradaARS += comisionARS;
                        }
                        
                        // Pago real al proveedor: SIEMPRE monto bruto - comisiones
                        pagadoProveedorNetoARS += Math.max(montoARS - comisionARS, 0);
                      } else {
                        // Sin comisión: pago completo al proveedor
                        pagadoProveedorNetoARS += montoARS;
                      }
                    });
                  }


                  // Calcular adicionales
                  if (adicionalesData) {
                    const tc = parseFloat(presupuesto.tipo_cambio || 1);
                    
                    adicionalesData.forEach((adicional) => {
                      // Para cliente siempre usar 'monto'
                      const montoClienteARS = presupuesto.moneda === "USD" ? adicional.monto * tc : adicional.monto;
                      
                      // Para proveedor, usar 'monto_real_proveedor' si existe (presupuestos con doble monto), 
                      // sino usar 'monto' (presupuestos normales)
                      const montoProveedorBase = adicional.monto_real_proveedor !== null && adicional.monto_real_proveedor !== undefined 
                        ? adicional.monto_real_proveedor 
                        : adicional.monto;
                      const montoProveedorARS = presupuesto.moneda === "USD" ? montoProveedorBase * tc : montoProveedorBase;
                      
                      // Sumar monto cliente solo al progreso cliente
                      adicionalesClienteARS += montoClienteARS;
                      
                      // Sumar monto proveedor solo al progreso proveedor (NO el monto cliente)
                      adicionalesProveedorARS += montoProveedorARS;
                    });
                  }

                  datosFinancieros = {
                    pagadoClienteARS,
                    pagadoProveedorNetoARS,
                    adicionalesClienteARS,
                    adicionalesProveedorARS,
                    comisionTotalARS,
                    comisionCobradaARS,
                  };
                } catch (finErr) {
                  console.error("Error cargando datos financieros:", finErr);
                }
              }

              return {
                ...presupuesto,
                total_calculado: totalData || presupuesto.monto_base,
                saldo_data: saldoData?.[0] || {
                  total_presupuesto: presupuesto.monto_base,
                  total_pagado: 0,
                  saldo_pendiente: presupuesto.monto_base,
                  porcentaje_pagado: 0,
                },
                datos_financieros: datosFinancieros,
              };
            } catch (err) {
              console.error("Error calculando datos del presupuesto:", err);
              return {
                ...presupuesto,
                total_calculado: presupuesto.monto_base,
                saldo_data: {
                  total_presupuesto: presupuesto.monto_base,
                  total_pagado: 0,
                  saldo_pendiente: presupuesto.monto_base,
                  porcentaje_pagado: 0,
                },
              };
            }
          })
        );

        setPresupuestos(presupuestosConCalculos);
      }

      // Solo cargar datos maestros si no es cliente
      if (!permisosCliente.esCliente) {
        const [
          proyectosResult,
          proveedoresResult,
          conceptosResult,
          etapasResult,
          subetapasResult,
          presupuestosInicialesResult,
        ] = await Promise.all([
          (async () => {
            if (filterVigencia === 'eliminados') {
              const { data: idsData, error: idsError } = await supabase
                .from('presupuestos')
                .select('proyecto_id')
                .eq('constructora_id', constructora.id)
                .or('activo.eq.false,deleted_at.not.is.null');
              if (idsError) return { data: [], error: idsError };
              const ids = Array.from(new Set((idsData || []).map(r => r.proyecto_id).filter(Boolean)));
              if (ids.length === 0) return { data: [], error: null };
              return await supabase
                .from('proyectos')
                .select('*')
                .eq('constructora_id', constructora.id)
                .in('id', ids)
                .order('codigo');
            } else {
              return await supabase
                .from('proyectos')
                .select('*')
                .eq('constructora_id', constructora.id)
                .eq('activo', true)
                .is('deleted_at', null)
                .eq('obra_finalizada', false)
                .order('codigo');
            }
          })(),
          supabase
            .from("proveedores_servicios")
            .select("*")
            .eq("constructora_id", constructora.id)
            .eq("activo", true)
            .order("nombre"),
          supabase
            .from("conceptos_salida")
            .select("*")
            .eq("constructora_id", constructora.id)
            .eq("activo", true)
            .order("nombre"),
          supabase
            .from("etapas_obra")
            .select("*")
            .eq("constructora_id", constructora.id)
            .eq("activo", true)
            .order("orden", { ascending: true }),
          supabase
            .from("subetapas_obra")
            .select("*")
            .eq("constructora_id", constructora.id)
            .eq("activo", true)
            .order("orden", { ascending: true }),
          supabase
            .from("presupuestos_iniciales")
            .select("*")
            .eq("constructora_id", constructora.id),
        ]);

        if (proyectosResult.error)
          console.error("Error cargando proyectos:", proyectosResult.error);
        else setProyectos(proyectosResult.data || []);

        if (proveedoresResult.error)
          console.error("Error cargando proveedores:", proveedoresResult.error);
        else setProveedores(proveedoresResult.data || []);

        if (conceptosResult.error)
          console.error("Error cargando conceptos:", conceptosResult.error);
        else setConceptosSalida(conceptosResult.data || []);

        if (etapasResult.error)
          console.error("Error cargando etapas:", etapasResult.error);
        else setEtapas(etapasResult.data || []);

        if (subetapasResult.error)
          console.error("Error cargando subetapas:", subetapasResult.error);
        else setSubetapas(subetapasResult.data || []);

        if (presupuestosInicialesResult.error)
          console.error(
            "Error cargando presupuestos iniciales:",
            presupuestosInicialesResult.error
          );
        else setPresupuestosIniciales(presupuestosInicialesResult.data || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoading(false);
  };

  // Función para cargar datos de mano de obra por proyecto
  const cargarDatosManoObra = async () => {
    console.log("🔍 cargarDatosManoObra llamada - activeTab:", activeTab);
    if (activeTab !== "mano_obra") {
      console.log("❌ No es pestaña mano_obra, saliendo");
      return;
    }
    
    try {
      console.log("🔍 Cargando datos de mano de obra...");
      
      // 1. Cargar presupuestos de mano de obra con relaciones
      const { data: presupuestosMO, error: errorPresupuestosMO } = await supabase
        .from("presupuestos")
        .select(`
          *,
          proveedores_servicios (nombre, cuit),
          adicionales_presupuesto (monto, monto_real_proveedor, activo)
        `)
        .eq("constructora_id", constructora.id)
        .eq("tipo_presupuesto", "mano_obra")
        .eq("activo", true)
        .is("deleted_at", null);

      console.log("📊 Presupuestos MO encontrados:", presupuestosMO);
      console.log("📊 Cantidad de presupuestos MO:", presupuestosMO?.length || 0);

      if (errorPresupuestosMO) {
        console.error("Error cargando presupuestos MO:", errorPresupuestosMO);
        return;
      }

      // 2. Agrupar por proyecto
      const proyectosConMO = {};
      (presupuestosMO || []).forEach(presupuesto => {
        if (!proyectosConMO[presupuesto.proyecto_id]) {
          proyectosConMO[presupuesto.proyecto_id] = [];
        }
        proyectosConMO[presupuesto.proyecto_id].push(presupuesto);
      });

      console.log("📊 Proyectos con MO:", Object.keys(proyectosConMO));

      // 3. Para cada proyecto, cargar datos detallados
      const datosPorProyecto = await Promise.all(
        Object.keys(proyectosConMO).map(async (proyectoId) => {
          
          // Usar los presupuestos ya cargados para este proyecto
          const presupuestosDelProyecto = proyectosConMO[proyectoId];
          console.log(`📋 Presupuestos MO para proyecto ${proyectoId}:`, presupuestosDelProyecto);

          // Cargar salidas (pagos a proveedores) - se cargarán por concepto específico de cada presupuesto
          const { data: salidasMO, error: errorSalidas } = await supabase
            .from("salidas")
            .select("*")
            .eq("constructora_id", constructora.id)
            .eq("proyecto_id", proyectoId)
            .eq("activo", true)
            .is("deleted_at", null);

          if (errorSalidas) {
            console.error("Error cargando salidas MO:", errorSalidas);
          }

          // Cargar entradas (movimientos de caja MO) con concepto
          const { data: entradasMO, error: errorEntradas } = await supabase
            .from("entradas")
            .select(`
              *,
              conceptos_entrada (nombre)
            `)
            .eq("constructora_id", constructora.id)
            .eq("proyecto_id", proyectoId)
            .eq("activo", true)
            .is("deleted_at", null);

          if (errorEntradas) {
            console.error("Error cargando entradas MO:", errorEntradas);
          }

          // Buscar presupuesto inicial actualizado para este proyecto
          const { data: piMO, error: errorPI } = await supabase
            .from("presupuestos_iniciales_actualizados")
            .select("*")
            .eq("constructora_id", constructora.id)
            .eq("proyecto_id", proyectoId)
            .eq("tipo", "mano_obra")
            .eq("activo", true)
            .maybeSingle();

          if (errorPI) {
            console.error("Error cargando PI MO:", errorPI);
          }

          console.log(`📊 PI MO para proyecto ${proyectoId}:`, piMO);

          // Calcular totales
          const calculos = calcularTotalesManoObra(
            piMO,
            presupuestosDelProyecto || [],
            salidasMO || [],
            entradasMO || [],
            cajas,
            dolarOficial,
            proyectoId
          );

          return {
            proyecto_id: proyectoId,
            presupuesto_inicial: piMO,
            presupuestos: presupuestosDelProyecto || [],
            calculos
          };
        })
      );

      const datosFinales = datosPorProyecto.filter(Boolean);
      console.log("✅ Datos finales de mano de obra:", datosFinales);
      console.log("✅ Cantidad de proyectos procesados:", datosFinales.length);
      setDatosManoObra(datosFinales);
    } catch (err) {
      console.error("Error cargando datos de mano de obra:", err);
    }
  };

  // Función para calcular totales de mano de obra
  const calcularTotalesManoObra = (piMO, presupuestos, salidas, entradas, cajas, dolarOficial, proyectoId) => {
    // 1. Calcular presupuestos reales (incluyendo adicionales)
    let totalPresupuestosReales = 0;
    let totalPagado = 0;

    // Mapear pagos por concepto para cada presupuesto
    const pagosPorPresupuesto = {};

    presupuestos.forEach(presupuesto => {
      // Presupuesto base
      totalPresupuestosReales += parseFloat(presupuesto.monto_base || 0);
      
      // Adicionales
      if (presupuesto.adicionales_presupuesto) {
        presupuesto.adicionales_presupuesto.forEach(adicional => {
          if (adicional.activo) {
            // Usar monto_real_proveedor si existe, sino monto
            const montoAdicional = adicional.monto_real_proveedor !== null && adicional.monto_real_proveedor !== undefined
              ? adicional.monto_real_proveedor
              : adicional.monto;
            totalPresupuestosReales += parseFloat(montoAdicional || 0);
          }
        });
      }

      // Calcular pagos específicos para este presupuesto por concepto Y proveedor
      const conceptoPresupuestoId = presupuesto.concepto_salida_id;
      const proveedorId = presupuesto.proveedor_id;
      let pagadoPresupuesto = 0;
      
      console.log(`🔍 Presupuesto ID ${presupuesto.id}: concepto_salida_id="${conceptoPresupuestoId}", proveedor_id=${proveedorId}`);
      
      salidas.forEach(salida => {
        // DEBUG: Verificar qué salidas se están evaluando
        console.log(`🔍 Evaluando salida: concepto_id="${salida.concepto_id}", proveedor_id=${salida.proveedor_id}, monto=${salida.monto}`);
        console.log(`🔍 Presupuesto espera: concepto_salida_id="${conceptoPresupuestoId}", proveedor_id=${proveedorId}`);
        
        if (salida.concepto_id === conceptoPresupuestoId && salida.proveedor_id === proveedorId) {
          const montoARS = salida.moneda === "USD" 
            ? parseFloat(salida.monto || 0) * parseFloat(salida.tipo_cambio || 1)
            : parseFloat(salida.monto || 0);
          pagadoPresupuesto += montoARS;
          console.log(`✅ Salida MATCH: ${montoARS} ARS agregado. Total presupuesto: ${pagadoPresupuesto}`);
        } else {
          console.log(`❌ Salida NO MATCH: concepto_id diferente o proveedor diferente`);
        }
      });

      pagosPorPresupuesto[presupuesto.id] = pagadoPresupuesto;
      totalPagado += pagadoPresupuesto;
    });

    // 3. Calcular comisiones
    const totalCliente = parseFloat(piMO?.total_actualizado_ars || 0);
    
    // Calcular lo que falta cobrarle al cliente (adelantos MO)
    let disponibleEnCaja = 0;
    let adelantosMO = 0;
    
    entradas.forEach(entrada => {
      const montoARS = entrada.moneda === "USD" 
        ? parseFloat(entrada.monto || 0) * parseFloat(entrada.tipo_cambio || 1)
        : parseFloat(entrada.monto || 0);

      const conceptoNombre = entrada.conceptos_entrada?.nombre || "";
      
      if (conceptoNombre.toLowerCase().includes("adelanto mo")) {
        disponibleEnCaja += montoARS;
        adelantosMO += montoARS;
      }
    });
    
    // Calcular lo que falta cobrarle al cliente
    const faltaCobrarCliente = totalCliente - disponibleEnCaja;
    
    // Calcular lo que falta pagar al proveedor
    const faltaPagarProveedor = totalPresupuestosReales - totalPagado;
    
    // Calcular comisiones cobradas (solo MO):
    // contar ÚNICAMENTE entradas con origen en cajas de mano_obra del proyecto y destino en cajas empresa
    let totalCobrado = 0;
    const idsCajasMOProyecto = (cajas || [])
      .filter(c => c.tipo_caja === "mano_obra" && c.proyecto_id === proyectoId)
      .map(c => c.id);
    const idsCajasEmpresa = (cajas || [])
      .filter(c => c.tipo_caja === "empresa")
      .map(c => c.id);

    entradas.forEach(entrada => {
      if (!entrada.caja_origen_id || !entrada.caja_destino_id) return;
      const esDesdeMOProyecto = idsCajasMOProyecto.includes(entrada.caja_origen_id);
      const esHaciaEmpresa = idsCajasEmpresa.includes(entrada.caja_destino_id);
      if (!esDesdeMOProyecto || !esHaciaEmpresa) return;

      const montoARS = entrada.moneda === "USD" 
        ? parseFloat(entrada.monto || 0) * parseFloat(entrada.tipo_cambio || 1)
        : parseFloat(entrada.monto || 0);
      totalCobrado += montoARS;
    });
    
    // Calcular saldo real de cajas de mano de obra del proyecto
    let saldoRealCajasMO = 0;
    if (cajas && dolarOficial && proyectoId) {
      const cajasManoObraProyecto = cajas.filter(caja => 
        caja.tipo_caja === "mano_obra" && caja.proyecto_id === proyectoId
      );
      
      cajasManoObraProyecto.forEach(caja => {
        const saldoCaja = parseFloat(caja.balance_actual || 0);
        if (caja.moneda === "USD") {
          // Convertir USD a ARS usando dólar oficial compra
          saldoRealCajasMO += saldoCaja * parseFloat(dolarOficial.compra || 1);
        } else {
          saldoRealCajasMO += saldoCaja;
        }
      });
    }
    
    // TOTAL = (Lo que falta cobrarle al cliente) - (Lo que falta pagar al proveedor) + (Lo que ya cobramos) + (Lo que tenemos en caja)
    const totalComision = faltaCobrarCliente - faltaPagarProveedor + totalCobrado + saldoRealCajasMO;

    // 4. Calcular totales finales
    const saldoAPagar = totalPresupuestosReales - totalPagado;
    const totalACobrar = totalComision - totalCobrado;

    return {
      totalCliente,
      totalPresupuestosReales,
      totalPagado,
      saldoAPagar,
      totalComision,
      totalCobrado,
      disponibleEnCaja,
      adelantosMO,
      saldoRealCajasMO,
      totalACobrar,
      pagosPorPresupuesto
    };
  };

  // Función para alternar expansión de proyecto
  const toggleProyectoExpandido = (proyectoId) => {
    setProyectosExpandidos(prev => {
      const nuevo = new Set(prev);
      if (nuevo.has(proyectoId)) {
        nuevo.delete(proyectoId);
      } else {
        nuevo.add(proyectoId);
      }
      return nuevo;
    });
  };

  const cargarDolarBlue = async () => {
    setLoadingDolar(true);
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/blue");
      const data = await response.json();
      setDolarBlue(data.venta);
    } catch (error) {
      console.error("Error cargando dólar blue:", error);
      setDolarBlue(1200); // Fallback
    }
    setLoadingDolar(false);
  };

  const cargarCajas = async () => {
    try {
      const { data: cajasData, error: cajasError } = await supabase
        .from("cajas")
        .select("*")
        .eq("constructora_id", constructora.id)
        .is("deleted_at", null)
        .order("tipo_caja");

      if (cajasError) {
        console.error("Error cargando cajas:", cajasError);
      } else {
        setCajas(cajasData || []);
      }
    } catch (error) {
      console.error("Error cargando cajas:", error);
    }
  };

  const cargarDolarOficial = async () => {
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      setDolarOficial(data);
    } catch (error) {
      console.error("Error cargando dólar oficial:", error);
      setDolarOficial({ compra: 1000, venta: 1000 }); // Fallback
    }
  };

  const cargarDetallePresupuesto = async (presupuesto) => {
    try {
      // Cargar adicionales
      const { data: adicionalesData, error: adicionalesError } = await supabase
        .from("adicionales_presupuesto")
        .select("*")
        .eq("presupuesto_id", presupuesto.id)
        .order("created_at");

      if (adicionalesError) {
        console.error("Error cargando adicionales:", adicionalesError);
      } else {
        setAdicionales(adicionalesData || []);
      }

      // Obtener pagos relacionados usando función SQL mejorada
      const { data: pagosData, error: pagosError } = await supabase.rpc(
        "obtener_pagos_presupuesto",
        { presupuesto_uuid: presupuesto.id }
      );

      if (pagosError) {
        console.error("Error cargando pagos:", pagosError);
      } else {
        setPagosDetectados(pagosData || []);
      }

      setDetailPresupuesto(presupuesto);
      setShowDetailModal(true);
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const formatearMoneda = (monto, moneda) => {
    const numero = parseFloat(monto || 0);
    const formateado = numero.toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${moneda} $${formateado}`;
  };

  const calcularEquivalencia = (monto, moneda, tipoCambio = null) => {
    const cambio = tipoCambio || dolarBlue;
    if (!cambio || !monto) return "";

    const numero = parseFloat(monto);
    if (moneda === "ARS") {
      const usd = numero / cambio;
      return `≈ USD $${usd.toLocaleString("en-US", {
        maximumFractionDigits: 2,
      })}`;
    } else {
      const ars = numero * cambio;
      return `≈ ARS $${ars.toLocaleString("es-AR", {
        maximumFractionDigits: 2,
      })}`;
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case "pendiente":
        return "#f59e0b";
      case "aprobado":
        return "#3b82f6";
      case "en_ejecucion":
        return "#8b5cf6";
      case "finalizado":
        return "#10b981";
      default:
        return "#64748b";
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado) {
      case "pendiente":
        return "Pendiente";
      case "aprobado":
        return "Aprobado";
      case "en_ejecucion":
        return "En Ejecución";
      case "finalizado":
        return "Finalizado";
      default:
        return estado;
    }
  };

  const handleEdit = (presupuesto) => {
    if (permisosCliente.esCliente) {
      showCustomAlert(
        "error",
        "Acción No Permitida",
        "No tienes permisos para editar presupuestos."
      );
      return;
    }
    setEditingPresupuesto(presupuesto);
    setShowModal(true);
  };

  const handleDelete = async (presupuestoId) => {
    if (permisosCliente.esCliente) {
      showCustomAlert(
        "error",
        "Acción No Permitida",
        "No tienes permisos para eliminar presupuestos."
      );
      return;
    }

    showCustomAlert(
      "confirm",
      "¿Eliminar presupuesto?",
      "Se eliminará (soft delete) y también sus adicionales (recuperables). ¿Continuar?",
      async () => {
        try {
          const { error } = await supabase.rpc("rpc_soft_delete_presupuesto", {
            p_presupuesto_id: presupuestoId,
            p_usuario_id: usuario?.id || null,
            p_razon: null,
          });
          if (error) {
            console.error("Error eliminando presupuesto:", error);
            showCustomAlert("error", "Error", "Error al eliminar: " + error.message);
          } else {
            showCustomAlert("success", "Éxito", "Presupuesto eliminado correctamente");
            cargarDatos();
          }
        } catch (err) {
          console.error("Error:", err);
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      }
    );
  };

  const handleRestore = async (presupuestoId) => {
    showCustomAlert(
      "confirm",
      "¿Restaurar presupuesto?",
      "Se restaurará el presupuesto y sus adicionales. ¿Continuar?",
      async () => {
        try {
          const { error } = await supabase.rpc("rpc_restore_presupuesto", {
            p_presupuesto_id: presupuestoId,
            p_usuario_id: usuario?.id || null,
          });
          if (error) {
            showCustomAlert("error", "Error", "Error al restaurar: " + error.message);
          } else {
            showCustomAlert("success", "Éxito", "Presupuesto restaurado correctamente");
            cargarDatos();
          }
        } catch (err) {
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      }
    );
  };

  const handleAddAdicional = (presupuesto) => {
    if (permisosCliente.esCliente) {
      showCustomAlert(
        "error",
        "Acción No Permitida",
        "No tienes permisos para agregar adicionales."
      );
      return;
    }
    setDetailPresupuesto(presupuesto);
    setEditingAdicional(null);
    setShowAdicionalModal(true);
  };

  const handleEditAdicional = (adicional) => {
    if (permisosCliente.esCliente) {
      showCustomAlert(
        "error",
        "Acción No Permitida",
        "No tienes permisos para editar adicionales."
      );
      return;
    }
    setEditingAdicional(adicional);
    setShowAdicionalModal(true);
  };

  // Filtrar presupuestos
  const filteredPresupuestos = presupuestos.filter((presupuesto) => {
    const matchesSearch =
      presupuesto.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      presupuesto.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      presupuesto.proyectos?.codigo
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      presupuesto.proveedores_servicios?.nombre
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesProject =
      filterProject === "todos" || presupuesto.proyecto_id === filterProject;

    const matchesEstado =
      filterEstado === "todos" || presupuesto.estado === filterEstado;

    const matchesEtapa =
      filterEtapa === "todos" || presupuesto.etapa_id === filterEtapa;

    // Corte de seguridad: cada solapa solo ve su tipo
    const matchesTipo = presupuesto.tipo_presupuesto === activeTab;

    return matchesSearch && matchesProject && matchesEstado && matchesEtapa && matchesTipo;
  });

  // Si es cliente y no puede ver presupuestos, mostrar mensaje
  if (
    permisosCliente.esCliente &&
    !puedeAccederAVista(permisosCliente.permisos, "presupuestos")
  ) {
    return (
      <div
        style={{
          padding: isMobile ? "16px 12px 120px 12px" : "20px",
          maxWidth: "100%",
          margin: "0 auto",
          background: "#f8fafc",
          minHeight: "100vh",
          animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "60px 40px",
            textAlign: "center",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "2px solid #fef2f2",
            animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              background: "#fef2f2",
              borderRadius: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <XCircle size={32} color="#dc2626" />
          </div>
          <h3
            style={{
              fontSize: "24px",
              color: "#dc2626",
              marginBottom: "16px",
              fontWeight: "600",
            }}
          >
            Acceso Restringido
          </h3>
          <p
            style={{
              color: "#64748b",
              fontSize: "16px",
              lineHeight: "1.5",
            }}
          >
            {obtenerMensajeRestriccion(permisosCliente, "presupuestos")}
          </p>
        </div>

        <CustomModal
          isOpen={customModal.isOpen}
          onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
          type={customModal.type}
          title={customModal.title}
          message={customModal.message}
          onConfirm={customModal.onConfirm}
          isMobile={isMobile}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header unificado */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
            minHeight: "56px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px #2563eb40",
                flexShrink: 0,
              }}
            >
              <Receipt size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Presupuestos
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {permisosCliente.esCliente
                  ? `Presupuestos del proyecto ${permisosCliente.proyectoCodigo}`
                  : "Gestión de presupuestos por proyecto"}
              </p>
            </div>
          </div>

          {/* Solo mostrar botón si no es cliente */}
          {permisosCliente.esCliente === false && (
            <button
              onClick={() => {
                setEditingPresupuesto(null);
                setShowModal(true);
              }}
              style={{
                background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                color: "white",
                border: "none",
                padding: isMobile ? "12px 20px" : "14px 24px",
                borderRadius: "12px",
                fontSize: isMobile ? "14px" : "15px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 4px 12px #2563eb30",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                flexShrink: 0,
                minWidth: "max-content",
                whiteSpace: "nowrap",
              }}
              className="button-hover"
            >
              <Plus size={16} />
              Nuevo presupuesto
            </button>
          )}
        </div>

        {/* Filtros */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : permisosCliente.esCliente
              ? "2fr 1fr"
              : "1.5fr 1fr 1fr 1fr 1fr",
            gap: "12px",
            marginBottom: "20px",
          }}
        >
          {/* Buscador */}
          <div style={{ position: "relative" }}>
            <Search
              size={16}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#64748b",
                zIndex: 1,
              }}
            />
            <input
              type="text"
              placeholder="Buscar presupuestos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus={false}
              autoComplete="off"
              style={{
                width: "100%",
                padding: "14px 16px 14px 44px",
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                fontSize: "16px",
                WebkitAppearance: "none",
                appearance: "none",
                background: "white",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                outline: "none",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#2563eb";
                e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#f1f5f9";
                e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
              }}
            />
          </div>

          {/* Solo mostrar filtros si no es cliente */}
          {!permisosCliente.esCliente && (
            <>
              <CustomDropdown
                value={filterVigencia}
                onChange={setFilterVigencia}
                options={[
                  { value: "activos", label: "Activos" },
                  { value: "eliminados", label: "Eliminados" },
                ]}
                placeholder="Estado de vigencia"
                icon={Filter}
              />
              <CustomDropdown
                value={filterProject}
                onChange={(value) => {
                  setFilterProject(value);
                  guardarFiltroProyecto(value);
                }}
                options={[
                  { value: "todos", label: "Todos los proyectos" },
                  ...proyectos.map((proyecto) => ({
                    value: proyecto.id,
                    label: `${proyecto.codigo} - ${proyecto.apellido_cliente}`,
                  })),
                ]}
                placeholder="Filtrar por proyecto"
                icon={Building2}
              />

              <CustomDropdown
                value={filterEtapa}
                onChange={setFilterEtapa}
                options={[
                  { value: "todos", label: "Todas las etapas" },
                  ...etapas.map((etapa) => ({
                    value: etapa.id,
                    label: etapa.nombre,
                  })),
                ]}
                placeholder="Filtrar por etapa"
                icon={Layers}
              />

              <CustomDropdown
                value={filterEstado}
                onChange={setFilterEstado}
                options={[
                  { value: "todos", label: "Todos los estados" },
                  { value: "pendiente", label: "Pendiente" },
                  { value: "aprobado", label: "Aprobado" },
                  { value: "en_ejecucion", label: "En Ejecución" },
                  { value: "finalizado", label: "Finalizado" },
                ]}
                placeholder="Filtrar por estado"
                icon={Filter}
              />
            </>
          )}

          {/* Solo mostrar filtro de estado para clientes */}
          {permisosCliente.esCliente && (
            <CustomDropdown
              value={filterEstado}
              onChange={setFilterEstado}
              options={[
                { value: "todos", label: "Todos los estados" },
                { value: "pendiente", label: "Pendiente" },
                { value: "aprobado", label: "Aprobado" },
                { value: "en_ejecucion", label: "En Ejecución" },
                { value: "finalizado", label: "Finalizado" },
              ]}
              placeholder="Filtrar por estado"
              icon={Filter}
            />
          )}
        </div>
      </div>

      {/* Tabs - Solo mostrar si no es cliente */}
      {permisosCliente.esCliente === false && (
        <div
          style={{
            display: "flex",
            gap: "4px",
            marginBottom: "20px",
            background: "white",
            padding: "4px",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            animation: "slideInRight 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <button
            onClick={() => setActiveTab("gastos_generales")}
            style={{
              flex: 1,
              padding: isMobile ? "12px 16px" : "14px 20px",
              border: "none",
              borderRadius: "8px",
              fontSize: isMobile ? "13px" : "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              background:
                activeTab === "gastos_generales"
                  ? "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)"
                  : "transparent",
              color: activeTab === "gastos_generales" ? "white" : "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <Package size={16} />
            Gastos Generales
          </button>

          <button
            onClick={() => setActiveTab("mano_obra")}
            style={{
              flex: 1,
              padding: isMobile ? "12px 16px" : "14px 20px",
              border: "none",
              borderRadius: "8px",
              fontSize: isMobile ? "13px" : "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              background:
                activeTab === "mano_obra"
                  ? "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)"
                  : "transparent",
              color: activeTab === "mano_obra" ? "white" : "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <Users size={16} />
            Mano de Obra
          </button>
        </div>
      )}

      {/* Resumen de stats - Solo mostrar en DESKTOP y si no es cliente */}
      {!isMobile &&
        !permisosCliente.esCliente &&
        filteredPresupuestos.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "12px",
              marginBottom: "20px",
              animation: "fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {[
              {
                label: "Total Presupuestos",
                value: filteredPresupuestos.length,
                icon: Receipt,
                color: "#2563eb",
              },
              {
                label: "Finalizados",
                value: filteredPresupuestos.filter(
                  (p) => p.estado === "finalizado"
                ).length,
                icon: CheckCircle2,
                color: "#10b981",
              },
              {
                label: "En Ejecución",
                value: filteredPresupuestos.filter(
                  (p) => p.estado === "en_ejecucion"
                ).length,
                icon: Clock,
                color: "#2563eb",
              },
              // Cambiar la cuarta tarjeta según la pestaña activa
              activeTab === "mano_obra" ? {
                label: "Pendiente a Ganar",
                value: datosManoObraFiltrados.reduce((total, datosProyecto) => {
                  const { calculos } = datosProyecto;
                  return total + (calculos.totalComision - calculos.totalCobrado);
                }, 0),
                icon: TrendingUp,
                color: "#dc2626",
                isMonetary: true,
              } : activeTab === "gastos_generales" ? {
                label: "Pendiente a Ganar",
                value: filteredPresupuestos.reduce((total, presupuesto) => {
                  // Calcular comisión constructora igual que en las tarjetas individuales
                  if (presupuesto.tiene_doble_monto && presupuesto.datos_financieros) {
                    // Calcular comisión total (totalCliente - totalProveedor)
                    let totalCliente, totalProveedor, comisionTotal;
                    
                    if (presupuesto.datos_financieros.totalClienteARS !== undefined) {
                      totalCliente = presupuesto.datos_financieros.totalClienteARS;
                      totalProveedor = presupuesto.datos_financieros.totalProveedorARS;
                      comisionTotal = presupuesto.datos_financieros.comisionTotal;
                    } else {
                      const adicionalesCliente = presupuesto.datos_financieros?.adicionalesClienteARS || 0;
                      const adicionalesProveedor = presupuesto.datos_financieros?.adicionalesProveedorARS || 0;
                      const tc = parseFloat(presupuesto.tipo_cambio || 1);
                      const adicionalesClienteMoneda = presupuesto.moneda === "USD" ? adicionalesCliente / tc : adicionalesCliente;
                      const adicionalesProveedorMoneda = presupuesto.moneda === "USD" ? adicionalesProveedor / tc : adicionalesProveedor;
                      
                      totalCliente = parseFloat(presupuesto.monto_base || 0) + adicionalesClienteMoneda;
                      totalProveedor = parseFloat(presupuesto.monto_real_proveedor || 0) + adicionalesProveedorMoneda;
                      comisionTotal = totalCliente - totalProveedor;
                    }
                    
                    // Calcular comisión cobrada
                    const comisionCobrada = presupuesto.datos_financieros.comisionCobradaARS || 0;
                    
                    return total + (comisionTotal - comisionCobrada);
                  }
                  return total;
                }, 0),
                icon: TrendingUp,
                color: "#dc2626",
                isMonetary: true,
              } : {
                label: "Pendientes",
                value: filteredPresupuestos.filter(
                  (p) => p.estado === "pendiente"
                ).length,
                icon: AlertCircle,
                color: "#f59e0b",
              },
            ].map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div
                  key={index}
                  className="stat-card"
                  style={{
                    background: "white",
                    padding: "12px",
                    borderRadius: "12px",
                    textAlign: "center",
                    border: "1px solid #f1f5f9",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    minHeight: "80px",
                    animation: `slideInRight 0.${
                      8 + index
                    }s cubic-bezier(0.4, 0, 0.2, 1)`,
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      background: `${stat.color}10`,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 8px",
                    }}
                  >
                    <IconComponent size={16} color={stat.color} />
                  </div>
                  <p
                    style={{
                      fontSize: "10px",
                      color: "#64748b",
                      margin: "0 0 4px 0",
                      fontWeight: "500",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {stat.label}
                  </p>
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                      margin: 0,
                    }}
                  >
                    {stat.isMonetary ? formatearMoneda(stat.value, "ARS") : stat.value}
                  </p>
                </div>
              );
            })}
          </div>
        )}

      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 20px",
            background: "white",
            borderRadius: "16px",
            border: "1px solid #f1f5f9",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "2px solid #e2e8f0",
                borderTop: "2px solid #3b82f6",
                borderRadius: "50%",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "500",
              }}
            >
              Cargando...
            </p>
          </div>
        </div>
      ) : ((activeTab === "mano_obra" ? datosManoObraFiltrados.length === 0 : filteredPresupuestos.length === 0)) ? (
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: isMobile ? "40px 20px" : "60px 40px",
            textAlign: "center",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "2px dashed #cbd5e1",
            animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              background: getVistaColor("presupuestos").light,
              borderRadius: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <Receipt size={32} color={getVistaColor("presupuestos").solid} />
          </div>
          <h3
            style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#1e293b",
              marginBottom: "8px",
            }}
          >
            {searchTerm ||
            filterProject !== "todos" ||
            filterEstado !== "todos" ||
            filterEtapa !== "todos"
              ? "No se encontraron presupuestos"
              : permisosCliente.esCliente
              ? "No hay presupuestos para tu proyecto"
              : `No hay presupuestos de ${
                  activeTab === "gastos_generales"
                    ? "gastos generales"
                    : "mano de obra"
                }`}
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: "#64748b",
              marginBottom:
                searchTerm ||
                filterProject !== "todos" ||
                filterEstado !== "todos" ||
                filterEtapa !== "todos" ||
                permisosCliente.esCliente
                  ? "0"
                  : "24px",
            }}
          >
            {searchTerm ||
            filterProject !== "todos" ||
            filterEstado !== "todos" ||
            filterEtapa !== "todos"
              ? "Intenta cambiar los filtros de búsqueda"
              : permisosCliente.esCliente
              ? "La constructora aún no ha creado presupuestos para tu proyecto"
              : "Crea el primer presupuesto para comenzar a gestionar costos del proyecto"}
          </p>
          {/* Solo mostrar botón si no es cliente y no hay filtros */}
          {permisosCliente.esCliente === false &&
            !searchTerm &&
            filterProject === "todos" &&
            filterEstado === "todos" &&
            filterEtapa === "todos" && (
              <button
                onClick={() => {
                  setEditingPresupuesto(null);
                  setShowModal(true);
                }}
                style={{
                  background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                  color: "white",
                  border: "none",
                  padding: "14px 28px",
                  borderRadius: "10px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  margin: "0 auto",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                className="button-hover"
              >
                <Plus size={16} />
                Crear Primer Presupuesto
              </button>
            )}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fill, minmax(400px, 1fr))",
            gap: "16px",
            alignItems: "start",
            animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {activeTab === "mano_obra" ? (
            // Mostrar vista de mano de obra con sector general + lista de proveedores
            <ManoObraView
              datosManoObra={datosManoObraFiltrados}
              proyectos={proyectos}
              proyectosExpandidos={proyectosExpandidos}
              onToggleProyecto={toggleProyectoExpandido}
              formatearMoneda={formatearMoneda}
              isMobile={isMobile}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDetail={cargarDetallePresupuesto}
              onAddAdicional={handleAddAdicional}
              calcularEquivalencia={calcularEquivalencia}
              getEstadoColor={getEstadoColor}
              getEstadoLabel={getEstadoLabel}
              permisosCliente={permisosCliente}
            />
          ) : (
            // Mostrar tarjetas normales de gastos generales
            filteredPresupuestos.map((presupuesto, index) => (
              <PresupuestoCard
                key={presupuesto.id}
                presupuesto={presupuesto}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onRestore={handleRestore}
                filterVigencia={filterVigencia}
                onDetail={cargarDetallePresupuesto}
                onAddAdicional={handleAddAdicional}
                formatearMoneda={formatearMoneda}
                calcularEquivalencia={calcularEquivalencia}
                getEstadoColor={getEstadoColor}
                getEstadoLabel={getEstadoLabel}
                isMobile={isMobile}
                permisosCliente={permisosCliente}
                index={index}
              />
            ))
          )}
        </div>
      )}

      {/* Info del tipo actual - Solo mostrar en DESKTOP y si no es cliente y solo en activos - MOVIDO ABAJO */}
      {!isMobile && permisosCliente.esCliente === false && filterVigencia === "activos" && (
        <div
          style={{
            background: "white",
            padding: "16px 20px",
            borderRadius: "12px",
            marginTop: "32px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: `1px solid ${
              activeTab === "gastos_generales" ? "#bbf7d0" : "#fed7aa"
            }`,
            animation: "slideInRight 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {activeTab === "gastos_generales" ? (
                <Package size={16} color="#10b981" />
              ) : (
                <Users size={16} color="#f59e0b" />
              )}
            </div>
            <h3
              style={{
                margin: 0,
                fontSize: "14px",
                fontWeight: "600",
                color: "#1e293b",
              }}
            >
              {activeTab === "gastos_generales"
                ? "Gastos Generales"
                : "Mano de Obra"}
            </h3>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "12px",
              color: "#64748b",
              lineHeight: "1.4",
            }}
          >
            {activeTab === "gastos_generales"
              ? "Presupuestos de materiales, servicios y gastos generales del proyecto. Pagos desde cajas empresa/proyecto. Visibles para clientes en obras por administración."
              : "Presupuestos de mano de obra y personal. Pagos solo desde cajas de mano de obra. Solo visibles para la constructora, nunca para clientes."}
          </p>
        </div>
      )}

      {/* Modal Crear/Editar Presupuesto - Solo si no es cliente */}
      {showModal && permisosCliente.esCliente === false && (
        <PresupuestoModal
          constructora={constructora}
          proyectos={proyectos}
          proveedores={proveedores}
          conceptosSalida={conceptosSalida}
          etapas={etapas}
          subetapas={subetapas}
          presupuestosIniciales={presupuestosIniciales}
          presupuesto={editingPresupuesto}
          tipoPresupuesto={activeTab}
          dolarBlue={dolarBlue}
          onClose={() => {
            setShowModal(false);
            setEditingPresupuesto(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingPresupuesto(null);
            cargarDatos();
          }}
          formatearMoneda={formatearMoneda}
          calcularEquivalencia={calcularEquivalencia}
          verificarPresupuestoInicial={verificarPresupuestoInicial}
          isMobile={isMobile}
          showCustomAlert={showCustomAlert}
        />
      )}

      {/* Modal Detalle Presupuesto */}
      {showDetailModal && detailPresupuesto && (
        <PresupuestoDetailModal
          presupuesto={detailPresupuesto}
          adicionales={adicionales}
          pagosDetectados={pagosDetectados}
          onClose={() => {
            setShowDetailModal(false);
            setDetailPresupuesto(null);
          }}
          onEditAdicional={handleEditAdicional}
          onAddAdicional={handleAddAdicional}
          onReloadPresupuesto={cargarDetallePresupuesto}
          onReloadAllData={cargarDatos}
          formatearMoneda={formatearMoneda}
          calcularEquivalencia={calcularEquivalencia}
          isMobile={isMobile}
          permisosCliente={permisosCliente}
          showCustomAlert={showCustomAlert}
        />
      )}

      {/* Modal Adicional - Solo si no es cliente */}
      {showAdicionalModal &&
        detailPresupuesto &&
        permisosCliente.esCliente === false && (
          <AdicionalModal
            presupuesto={detailPresupuesto}
            adicional={editingAdicional}
            onClose={() => {
              setShowAdicionalModal(false);
              setEditingAdicional(null);
            }}
            onSave={() => {
              setShowAdicionalModal(false);
              setEditingAdicional(null);
              // Solo recargar todos los datos para actualizar gráficos
              setTimeout(() => {
                cargarDatos();
              }, 500);
            }}
            formatearMoneda={formatearMoneda}
            isMobile={isMobile}
            showCustomAlert={showCustomAlert}
          />
        )}

      {/* Modal personalizado para alertas */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        isMobile={isMobile}
      />

      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Hover effects */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </div>
  );
}

// Componente para cada tarjeta de presupuesto con permisos
function PresupuestoCard({
  presupuesto,
  onEdit,
  onDelete,
  onRestore,
  filterVigencia,
  onDetail,
  onAddAdicional,
  formatearMoneda,
  calcularEquivalencia,
  getEstadoColor,
  getEstadoLabel,
  isMobile,
  permisosCliente,
  index,
  isProyectoMO = false,
  onToggleExpandir,
  isExpanded = false,
}) {
  const saldoData = presupuesto.saldo_data || {};
  const total = parseFloat(
    saldoData.total_presupuesto || presupuesto.monto_base || 0
  );
  const totalPagado = parseFloat(saldoData.total_pagado || 0);
  const saldoPendienteCalculado = Math.max(0, total - totalPagado);
  const saldoPendiente = saldoPendienteCalculado;
  const porcentajePagado = total > 0 ? (totalPagado / total) * 100 : 0;
  const estadoColor = getEstadoColor(presupuesto.estado);

  return (
    <div
      style={{
        background: "white",
        borderRadius: "16px",
        padding: isMobile ? "14px" : "16px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        border: "1px solid #f1f5f9",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `slideInRight 0.${
          5 + (index % 10)
        }s cubic-bezier(0.4, 0, 0.2, 1)`,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "12px",
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "4px",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "600",
                color: "#1e293b",
                lineHeight: "1.2",
              }}
            >
              {presupuesto.titulo}
            </h3>
            {presupuesto.codigo && (
              <span
                style={{
                  background: "#f1f5f9",
                  color: "#64748b",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontSize: "10px",
                  fontWeight: "500",
                }}
              >
                {presupuesto.codigo}
              </span>
            )}
          </div>
          <p
            style={{
              margin: "0 0 8px 0",
              fontSize: "13px",
              color: "#64748b",
            }}
          >
            {presupuesto.proyectos?.codigo} -{" "}
            {presupuesto.proyectos?.apellido_cliente}
          </p>
        </div>
        <div
          style={{
            background: estadoColor,
            color: "white",
            padding: "4px 8px",
            borderRadius: "12px",
            fontSize: "11px",
            fontWeight: "600",
            textAlign: "center",
          }}
        >
          {getEstadoLabel(presupuesto.estado)}
        </div>
      </div>

      {/* Proveedor y concepto - solo para presupuestos normales, no para proyectos de mano de obra */}
      {!isProyectoMO && (
        <div
          style={{
            background: "#f8fafc",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "12px",
          }}
        >
          <p
            style={{
              margin: "0 0 4px 0",
              fontSize: "12px",
              color: "#64748b",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <User size={12} />
            Proveedor
          </p>
          <p
            style={{
              margin: "0 0 8px 0",
              fontSize: "14px",
              fontWeight: "600",
              color: "#1e293b",
            }}
          >
            {presupuesto.proveedores_servicios?.nombre}
          </p>
          <p
            style={{
              margin: "0 0 4px 0",
              fontSize: "12px",
              color: "#64748b",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Package size={12} />
            Concepto
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "13px",
              color: "#374151",
            }}
          >
            {presupuesto.conceptos_salida?.nombre}
          </p>
        </div>
      )}

      {/* Etapa y Subetapa - NUEVO */}
      {(presupuesto.etapas_obra || presupuesto.subetapas_obra) && (
        <div
          style={{
            background: "#f3e8ff",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "12px",
          }}
        >
          {presupuesto.etapas_obra && (
            <div
              style={{ marginBottom: presupuesto.subetapas_obra ? "8px" : "0" }}
            >
              <p
                style={{
                  margin: "0 0 4px 0",
                  fontSize: "12px",
                  color: "#7c3aed",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Layers size={12} />
                Etapa
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#6b21a8",
                }}
              >
                {presupuesto.etapas_obra.nombre}
              </p>
            </div>
          )}
          {presupuesto.subetapas_obra && (
            <div>
              <p
                style={{
                  margin: "0 0 4px 0",
                  fontSize: "12px",
                  color: "#7c3aed",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Target size={12} />
                Subetapa
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#6b21a8",
                }}
              >
                {presupuesto.subetapas_obra.nombre}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Progreso de Pagos - solo para presupuestos SIN doble monto */}
      {!presupuesto.tiene_doble_monto && (
        <div
          style={{
            background: "#f8fafc",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <span
              style={{
                fontSize: "12px",
                color: "#64748b",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <BarChart3 size={12} />
              Progreso de Pagos
            </span>
            <span
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: porcentajePagado >= 100 ? "#059669" : "#f59e0b",
              }}
            >
              {porcentajePagado.toFixed(1)}%
            </span>
          </div>

          {/* Barra de progreso */}
          <div
            style={{
              background: "#e2e8f0",
              height: "6px",
              borderRadius: "3px",
              overflow: "hidden",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                height: "100%",
                background:
                  porcentajePagado >= 100
                    ? "linear-gradient(135deg, #059669 0%, #16a34a 100%)"
                    : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                width: `${Math.min(porcentajePagado, 100)}%`,
                transition: "width 0.3s ease",
              }}
            ></div>
          </div>

          {/* Montos */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px",
              fontSize: "11px",
            }}
          >
            <div>
              <span style={{ color: "#64748b" }}>Pagado: </span>
              <span style={{ color: "#059669", fontWeight: "600" }}>
                {formatearMoneda(totalPagado, presupuesto.moneda)}
              </span>
            </div>
            <div>
              <span style={{ color: "#64748b" }}>Pendiente: </span>
              <span
                style={{
                  color: saldoPendiente > 0 ? "#dc2626" : "#059669",
                  fontWeight: "600",
                }}
              >
                {formatearMoneda(saldoPendiente, presupuesto.moneda)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Calcular totales con adicionales */}
      {(() => {
        // Solo calcular adicionales si tiene doble monto, sino usar montos base
        let totalCliente, totalProveedor, comisionTotal;
        
        if (presupuesto.tiene_doble_monto) {
          // Si tenemos datos financieros pre-calculados (para proyectos MO), usarlos
          if (presupuesto.datos_financieros?.totalClienteARS !== undefined) {
            totalCliente = presupuesto.datos_financieros.totalClienteARS;
            totalProveedor = presupuesto.datos_financieros.totalProveedorARS;
            comisionTotal = presupuesto.datos_financieros.comisionTotal;
          } else {
            // Obtener adicionales de datos financieros
            const adicionalesCliente = presupuesto.datos_financieros?.adicionalesClienteARS || 0;
            const adicionalesProveedor = presupuesto.datos_financieros?.adicionalesProveedorARS || 0;
            
            // Convertir adicionales a moneda del presupuesto
            const tc = parseFloat(presupuesto.tipo_cambio || 1);
            const adicionalesClienteMoneda = presupuesto.moneda === "USD" ? adicionalesCliente / tc : adicionalesCliente;
            const adicionalesProveedorMoneda = presupuesto.moneda === "USD" ? adicionalesProveedor / tc : adicionalesProveedor;
            
            // Calcular totales simples
            totalCliente = parseFloat(presupuesto.monto_base || 0) + adicionalesClienteMoneda;
            totalProveedor = parseFloat(presupuesto.monto_real_proveedor || 0) + adicionalesProveedorMoneda;
            comisionTotal = totalCliente - totalProveedor;
          }
        } else {
          // Para presupuestos normales, también sumar adicionales por separado
          const adicionalesCliente = presupuesto.datos_financieros?.adicionalesClienteARS || 0;
          const adicionalesProveedor = presupuesto.datos_financieros?.adicionalesProveedorARS || 0;
          const tc = parseFloat(presupuesto.tipo_cambio || 1);
          const adicionalesClienteMoneda = presupuesto.moneda === "USD" ? adicionalesCliente / tc : adicionalesCliente;
          const adicionalesProveedorMoneda = presupuesto.moneda === "USD" ? adicionalesProveedor / tc : adicionalesProveedor;
          
          totalCliente = parseFloat(presupuesto.monto_base || 0) + adicionalesClienteMoneda;
          totalProveedor = parseFloat(presupuesto.monto_base || 0) + adicionalesProveedorMoneda;
          comisionTotal = totalCliente - totalProveedor;
        }

        return (
          <>
            {/* Gráficos de progreso detallados - solo si tiene doble monto */}
            {presupuesto.tiene_doble_monto && (
              <div style={{ marginBottom: "12px", display: "grid", gap: "8px" }}>
                {(() => {
                  let pagadoCliente = totalPagado;
                  let pagadoProveedorNeto = totalPagado * 0.85; // Estimación si no hay datos reales
                  let comisionCobrada = Math.max(pagadoCliente - pagadoProveedorNeto, 0);

                  // Usar datos reales si están disponibles
                  if (presupuesto.datos_financieros) {
                    const tc = parseFloat(presupuesto.tipo_cambio || 1);
                    pagadoCliente = presupuesto.moneda === "USD" 
                      ? presupuesto.datos_financieros.pagadoClienteARS / tc 
                      : presupuesto.datos_financieros.pagadoClienteARS;
                    pagadoProveedorNeto = presupuesto.moneda === "USD" 
                      ? presupuesto.datos_financieros.pagadoProveedorNetoARS / tc 
                      : presupuesto.datos_financieros.pagadoProveedorNetoARS;
                    
                    // Usar comisionCobrada pre-calculado si está disponible
                    if (presupuesto.datos_financieros.comisionCobradaARS !== undefined) {
                      comisionCobrada = presupuesto.moneda === "USD" 
                        ? presupuesto.datos_financieros.comisionCobradaARS / tc 
                        : presupuesto.datos_financieros.comisionCobradaARS;
                    } else {
                      comisionCobrada = Math.max(pagadoCliente - pagadoProveedorNeto, 0);
                    }
                  }

                  // Calcular porcentajes (usar pre-calculados si están disponibles)
                  const porcentajeCliente = presupuesto.datos_financieros?.porcentajeCliente !== undefined 
                    ? presupuesto.datos_financieros.porcentajeCliente
                    : (totalCliente > 0 ? (pagadoCliente / totalCliente) * 100 : 0);
                  const porcentajeProveedor = presupuesto.datos_financieros?.porcentajeProveedor !== undefined 
                    ? presupuesto.datos_financieros.porcentajeProveedor
                    : (totalProveedor > 0 ? (pagadoProveedorNeto / totalProveedor) * 100 : 0);
                  const porcentajeComision = presupuesto.datos_financieros?.porcentajeComision !== undefined 
                    ? presupuesto.datos_financieros.porcentajeComision
                    : (comisionTotal > 0 ? (comisionCobrada / comisionTotal) * 100 : 0);

                  return (
                    <>
                {/* 1. Progreso Cliente */}
                <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "8px", marginBottom: "8px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "500", display: "flex", alignItems: "center", gap: "4px" }}>
                      <User size={12} />
                      {permisosCliente.esCliente ? "Progreso" : "Presupuesto total cliente"}
                    </span>
                    <span style={{ fontSize: "12px", fontWeight: "600", color: porcentajeCliente >= 100 ? "#059669" : "#f59e0b" }}>
                      {porcentajeCliente.toFixed(1)}%
                    </span>
                  </div>
                  <div style={{ background: "#e2e8f0", height: "6px", borderRadius: "3px", overflow: "hidden", marginBottom: "8px" }}>
                    <div style={{
                      height: "100%",
                      background: porcentajeCliente >= 100 ? "linear-gradient(135deg, #059669 0%, #16a34a 100%)" : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      width: `${Math.min(porcentajeCliente, 100)}%`,
                      transition: "width 0.3s ease",
                    }}></div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", fontSize: "11px" }}>
                    <div>
                      <span style={{ color: "#64748b" }}>Total: </span>
                      <span style={{ color: "#1e293b", fontWeight: "600" }}>{formatearMoneda(totalCliente, presupuesto.moneda)}</span>
                    </div>
                    <div>
                      <span style={{ color: "#64748b" }}>Pagado: </span>
                      <span style={{ color: "#059669", fontWeight: "600" }}>{formatearMoneda(pagadoCliente, presupuesto.moneda)}</span>
                    </div>
                    <div>
                      <span style={{ color: "#64748b" }}>Pendiente: </span>
                      <span style={{ color: Math.max(totalCliente - pagadoCliente, 0) > 0 ? "#dc2626" : "#059669", fontWeight: "600" }}>
                        {formatearMoneda(Math.max(totalCliente - pagadoCliente, 0), presupuesto.moneda)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 2. Progreso Proveedor - solo para no clientes */}
                {!permisosCliente.esCliente && (
                <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "8px", marginBottom: "8px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "500", display: "flex", alignItems: "center", gap: "4px" }}>
                      <Truck size={12} />
                      Progreso proveedores
                    </span>
                    <span style={{ fontSize: "12px", fontWeight: "600", color: porcentajeProveedor >= 100 ? "#059669" : "#f59e0b" }}>
                      {porcentajeProveedor.toFixed(1)}%
                    </span>
                  </div>
                  <div style={{ background: "#e2e8f0", height: "6px", borderRadius: "3px", overflow: "hidden", marginBottom: "8px" }}>
                    <div style={{
                      height: "100%",
                      background: porcentajeProveedor >= 100 ? "linear-gradient(135deg, #059669 0%, #16a34a 100%)" : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      width: `${Math.min(porcentajeProveedor, 100)}%`,
                      transition: "width 0.3s ease",
                    }}></div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", fontSize: "11px" }}>
                    <div>
                      <span style={{ color: "#64748b" }}>Total: </span>
                      <span style={{ color: "#1e293b", fontWeight: "600" }}>{formatearMoneda(totalProveedor, presupuesto.moneda)}</span>
                    </div>
                    <div>
                      <span style={{ color: "#64748b" }}>Pagado: </span>
                      <span style={{ color: "#059669", fontWeight: "600" }}>{formatearMoneda(pagadoProveedorNeto, presupuesto.moneda)}</span>
                    </div>
                    <div>
                      <span style={{ color: "#64748b" }}>Pendiente: </span>
                      <span style={{ color: Math.max(totalProveedor - pagadoProveedorNeto, 0) > 0 ? "#dc2626" : "#059669", fontWeight: "600" }}>
                        {formatearMoneda(Math.max(totalProveedor - pagadoProveedorNeto, 0), presupuesto.moneda)}
                      </span>
                    </div>
                  </div>
                </div>
                )}

                {/* 3. Progreso Comisión Constructora - solo para no clientes */}
                {!permisosCliente.esCliente && comisionTotal > 0 && (
                  <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "8px", marginBottom: "8px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "500", display: "flex", alignItems: "center", gap: "4px" }}>
                        <Building2 size={12} />
                        Comisión constructora
                      </span>
                      <span style={{ fontSize: "12px", fontWeight: "600", color: porcentajeComision >= 100 ? "#059669" : "#f59e0b" }}>
                        {porcentajeComision.toFixed(1)}%
                      </span>
                    </div>
                    <div style={{ background: "#e2e8f0", height: "6px", borderRadius: "3px", overflow: "hidden", marginBottom: "8px" }}>
                      <div style={{
                        height: "100%",
                        background: porcentajeComision >= 100 ? "linear-gradient(135deg, #059669 0%, #16a34a 100%)" : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        width: `${Math.min(porcentajeComision, 100)}%`,
                        transition: "width 0.3s ease",
                      }}></div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", fontSize: "11px" }}>
                      <div>
                        <span style={{ color: "#64748b" }}>Total: </span>
                        <span style={{ color: "#1e293b", fontWeight: "600" }}>{formatearMoneda(comisionTotal, presupuesto.moneda)}</span>
                      </div>
                      <div>
                        <span style={{ color: "#64748b" }}>Cobrado: </span>
                        <span style={{ color: "#059669", fontWeight: "600" }}>{formatearMoneda(comisionCobrada, presupuesto.moneda)}</span>
                      </div>
                      <div>
                        <span style={{ color: "#64748b" }}>Pendiente: </span>
                        <span style={{ color: Math.max(comisionTotal - comisionCobrada, 0) > 0 ? "#dc2626" : "#059669", fontWeight: "600" }}>
                          {formatearMoneda(Math.max(comisionTotal - comisionCobrada, 0), presupuesto.moneda)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Monto Total */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "8px",
        }}
      >
        {presupuesto.tiene_doble_monto ? (
          <>
            {/* Dos montos para presupuestos con comisión */}
            {permisosCliente.esCliente ? (
              /* Solo monto para clientes */
              <div style={{ textAlign: "center", marginBottom: "8px" }}>
                <p style={{ margin: "0 0 2px 0", fontSize: "10px", color: "#64748b", fontWeight: "500" }}>
                  Monto
                </p>
                <p style={{ margin: "0 0 2px 0", fontSize: isMobile ? "16px" : "18px", fontWeight: "700", color: "#059669" }}>
                  {formatearMoneda(totalCliente, presupuesto.moneda)}
                </p>
                <p style={{ margin: "0", fontSize: "10px", color: "#64748b" }}>
                  {calcularEquivalencia(
                    totalCliente,
                    presupuesto.moneda,
                    presupuesto.tipo_cambio
                  )}
                </p>
              </div>
            ) : (
              /* Ambos montos para no clientes */
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "8px" }}>
                <div>
                  <p style={{ margin: "0 0 2px 0", fontSize: "10px", color: "#64748b", fontWeight: "500" }}>
                    Monto
                  </p>
                  <p style={{ margin: "0 0 2px 0", fontSize: isMobile ? "16px" : "18px", fontWeight: "700", color: "#059669" }}>
                    {formatearMoneda(totalCliente, presupuesto.moneda)}
                  </p>
                  <p style={{ margin: "0", fontSize: "10px", color: "#64748b" }}>
                    {calcularEquivalencia(
                      totalCliente,
                      presupuesto.moneda,
                      presupuesto.tipo_cambio
                    )}
                  </p>
                </div>
                <div>
                  <p style={{ margin: "0 0 2px 0", fontSize: "10px", color: "#64748b", fontWeight: "500" }}>
                    Monto Real Proveedor
                  </p>
                  <p style={{ margin: "0 0 2px 0", fontSize: isMobile ? "16px" : "18px", fontWeight: "700", color: "#0369a1" }}>
                    {formatearMoneda(totalProveedor, presupuesto.moneda)}
                  </p>
                  <p style={{ margin: "0", fontSize: "10px", color: "#64748b" }}>
                    {calcularEquivalencia(
                      totalProveedor,
                      presupuesto.moneda,
                      presupuesto.tipo_cambio
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Monto único para presupuestos normales */}
            <p
              style={{
                margin: "0 0 4px 0",
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "700",
                color: "#059669",
              }}
            >
              {formatearMoneda(total, presupuesto.moneda)}
            </p>
            <p
              style={{
                margin: "0 0 8px 0",
                fontSize: "11px",
                color: "#64748b",
              }}
            >
              {calcularEquivalencia(
                total,
                presupuesto.moneda,
                presupuesto.tipo_cambio
              )}
            </p>
          </>
        )}
        {presupuesto.fecha_presupuesto && (
          <p
            style={{
              margin: "4px 0 0 0",
              fontSize: "10px",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
            }}
          >
            <Calendar size={10} />
            {formatDateForDisplay(presupuesto.fecha_presupuesto)}
          </p>
        )}
      </div>

      {/* Acciones según permisos */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: permisosCliente.esCliente
            ? "1fr"
            : "1fr 1fr 1fr",
          gap: "8px",
        }}
      >
        {isProyectoMO ? (
          <button
            onClick={onToggleExpandir}
            style={{
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              background: "white",
              color: "#374151",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#f8fafc";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "white";
            }}
          >
            <ChevronDown 
              size={12} 
              style={{
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
            {isExpanded ? "Ocultar proveedores" : "Ver proveedores"}
          </button>
        ) : (
          <button
            onClick={() => onDetail(presupuesto)}
            style={{
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              background: "white",
              color: "#374151",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#f8fafc";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "white";
            }}
          >
            <Eye size={12} />
            Ver
          </button>
        )}

        {/* Solo mostrar botones de edición si no es cliente y el presupuesto está activo y no es proyecto MO */}
        {!permisosCliente.esCliente && filterVigencia === "activos" && !isProyectoMO && (
          <>
            <button
              onClick={() => onEdit(presupuesto)}
              style={{
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                background: "white",
                color: "#374151",
                fontSize: "12px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#f0f9ff";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "white";
              }}
            >
              <Edit size={12} />
              Editar
            </button>

            <button
              onClick={() => onAddAdicional(presupuesto)}
              style={{
                padding: "8px 12px",
                border: "1px solid #10b981",
                borderRadius: "8px",
                background: "#10b981",
                color: "white",
                fontSize: "12px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#059669";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "#10b981";
              }}
            >
              <Plus size={12} />
              Adicional
            </button>
          </>
        )}
      </div>

      {/* Acciones de vigencia - Solo si no es cliente */}
      {!permisosCliente.esCliente && (
        filterVigencia === "activos" ? (
          <button
            onClick={() => onDelete(presupuesto.id)}
            style={{
              width: "100%",
              marginTop: "8px",
              padding: "6px",
              border: "none",
              borderRadius: "6px",
              background: "#fef2f2",
              color: "#dc2626",
              fontSize: "11px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#fee2e2";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#fef2f2";
            }}
          >
            <Trash2 size={10} />
            Eliminar
          </button>
        ) : (
          <button
            onClick={() => onRestore(presupuesto.id)}
            style={{
              width: "100%",
              marginTop: "8px",
              padding: "6px",
              border: "none",
              borderRadius: "6px",
              background: "#f0fdf4",
              color: "#16a34a",
              fontSize: "11px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#dcfce7";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#f0fdf4";
            }}
          >
            <Package size={10} />
            Restaurar
          </button>
        )
      )}
            </>
          );
        })()}
    </div>
  );
}

// Modal para crear/editar presupuesto con etapas y subetapas
function PresupuestoModal({
  constructora,
  proyectos,
  proveedores,
  conceptosSalida,
  etapas,
  subetapas,
  presupuestosIniciales,
  presupuesto,
  tipoPresupuesto,
  dolarBlue,
  onClose,
  onSave,
  formatearMoneda,
  calcularEquivalencia,
  verificarPresupuestoInicial,
  isMobile,
  showCustomAlert,
}) {
  const [currentStep, setCurrentStep] = useState(1); // SIEMPRE empieza en paso 1
  const [guardando, setGuardando] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const [formData, setFormData] = useState({
    proyecto_id: "",
    proveedor_id: "",
    concepto_salida_id: "",
    etapa_id: "",
    subetapa_id: "",
    codigo: "",
    titulo: "",
    descripcion: "",
    monto_base: "",
    moneda: "ARS",
    tipo_cambio: dolarBlue || 1200,
    monto_real_proveedor: "",
    tiene_doble_monto: false,
    fecha_presupuesto: new Date().toISOString().split("T")[0],
    comprobante_url: null,
    tiene_comprobante: false,
  });

  // Estados para filtrado de subetapas
  const [subetapasFiltradas, setSubetapasFiltradas] = useState([]);
  // Resumen financiero (solo constructora)
  const [resumenFinanciero, setResumenFinanciero] = useState(null);

  const calcularARS = (monto, moneda, tcFila) => {
    const montoNum = parseFloat(monto || 0) || 0;
    if (!moneda || moneda === "ARS") return montoNum;
    const tc = parseFloat(tcFila || formData.tipo_cambio || 1) || 1;
    return montoNum * tc;
  };

  const cargarResumenFinanciero = async () => {
    try {
      if (!constructora?.id || !formData.proyecto_id || !formData.proveedor_id || !formData.concepto_salida_id) {
        setResumenFinanciero(null);
        return;
      }

      const { data, error } = await supabase
        .from("salidas")
        .select("monto, moneda, tipo_cambio, tiene_comision, monto_comision, comision_retencion_en_origen, comision_cobrada, activo, deleted_at")
        .eq("constructora_id", constructora.id)
        .eq("proyecto_id", formData.proyecto_id)
        .eq("proveedor_id", formData.proveedor_id)
        .eq("concepto_id", formData.concepto_salida_id)
        .eq("activo", true)
        .is("deleted_at", null);

      if (error) {
        console.error("Error cargando resumen financiero:", error);
        setResumenFinanciero(null);
        return;
      }

      let pagadoClienteARS = 0;
      let pagadoProveedorNetoARS = 0;
      let comisionTotalARS = 0;
      let comisionCobradaARS = 0;

      (data || []).forEach((row) => {
        const montoARS = calcularARS(row.monto, row.moneda, row.tipo_cambio);
        pagadoClienteARS += montoARS;

        const montoComisionARS = row.tiene_comision
          ? calcularARS(row.monto_comision || 0, row.moneda, row.tipo_cambio)
          : 0;
        comisionTotalARS += montoComisionARS;
        if (row.tiene_comision && (row.comision_retencion_en_origen || row.comision_cobrada)) {
          comisionCobradaARS += montoComisionARS;
        }

        const netoProveedor = row.tiene_comision && row.comision_retencion_en_origen
          ? montoARS - montoComisionARS
          : montoARS;
        pagadoProveedorNetoARS += netoProveedor;
      });

      const comisionPendienteARS = Math.max(comisionTotalARS - comisionCobradaARS, 0);

      setResumenFinanciero({
        pagadoClienteARS,
        pagadoProveedorNetoARS,
        comisionTotalARS,
        comisionCobradaARS,
        comisionPendienteARS,
      });
    } catch (err) {
      console.error("Error resumen financiero:", err);
      setResumenFinanciero(null);
    }
  };

  useEffect(() => {
    cargarResumenFinanciero();
  }, [constructora?.id, formData.proyecto_id, formData.proveedor_id, formData.concepto_salida_id]);

  useEffect(() => {
    if (presupuesto) {
      // Modo edición - SIEMPRE empezar desde paso 1
      setFormData({
        proyecto_id: presupuesto.proyecto_id,
        proveedor_id: presupuesto.proveedor_id,
        concepto_salida_id: presupuesto.concepto_salida_id,
        etapa_id: presupuesto.etapa_id || "",
        subetapa_id: presupuesto.subetapa_id || "",
        codigo: presupuesto.codigo || "",
        titulo: presupuesto.titulo,
        descripcion: presupuesto.descripcion || "",
        monto_base: formatNumber(presupuesto.monto_base.toString()),
        moneda: presupuesto.moneda,
        tipo_cambio: presupuesto.tipo_cambio || dolarBlue || 1200,
        monto_real_proveedor: tipoPresupuesto === "mano_obra" 
          ? formatNumber(presupuesto.monto_base.toString())
          : (presupuesto.monto_real_proveedor != null ? formatNumber(String(presupuesto.monto_real_proveedor)) : ""),
        tiene_doble_monto: tipoPresupuesto === "mano_obra" ? false : !!presupuesto.tiene_doble_monto,
        fecha_presupuesto: formatDateForInput(presupuesto.fecha_presupuesto),
        comprobante_url: presupuesto.comprobante_url,
        tiene_comprobante: !!presupuesto.comprobante_url,
      });
      setCurrentStep(1); // CORREGIDO: Siempre empezar desde paso 1 también en edición
      // DEBUG
      console.log("[DEBUG PresupuestoModal] Abriendo modal edición:", {
        presupuesto,
        etapa_id: presupuesto.etapa_id,
        subetapa_id: presupuesto.subetapa_id,
      });
    } else {
      // Modo creación - actualizar tipo cambio
      setFormData((prev) => ({
        ...prev,
        tipo_cambio: dolarBlue || 1200,
      }));
    }
  }, [presupuesto, dolarBlue]);

  // Filtrar subetapas cuando cambia la etapa
  useEffect(() => {
    if (formData.etapa_id) {
      const subetapasParaEtapa = subetapas.filter(
        (s) => s.etapa_id === formData.etapa_id
      );
              setSubetapasFiltradas(subetapasParaEtapa);

        // Si la subetapa actual NO existe en el array global de subetapas, limpiarla
        if (
          formData.subetapa_id &&
          !subetapas.find((s) => String(s.id) === String(formData.subetapa_id))
        ) {
          setFormData((prev) => ({ ...prev, subetapa_id: "" }));
        }
      } else {
        setSubetapasFiltradas([]);
        setFormData((prev) => ({ ...prev, subetapa_id: "" }));
      }
  }, [formData.etapa_id, subetapas]);

  // Generar código automático cuando se completa paso 1
  const generarCodigoAutomatico = async () => {
    if (!presupuesto) {
      // Solo para nuevos presupuestos
      try {
        const { data, error } = await supabase.rpc(
          "generar_codigo_presupuesto",
          {
            constructora_uuid: constructora.id,
            tipo_presupuesto_param: tipoPresupuesto,
          }
        );

        if (error) {
          console.error("Error generando código:", error);
        } else {
          setFormData((prev) => ({ ...prev, codigo: data }));
        }
      } catch (err) {
        console.error("Error:", err);
      }
    }
  };

  const handleNext = async () => {
    if (validateStep(currentStep)) {
      if (currentStep === 1) {
        await generarCodigoAutomatico();
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.proyecto_id) {
          showCustomAlert("error", "Error", "Por favor selecciona un proyecto");
          return false;
        }
        if (!formData.proveedor_id) {
          showCustomAlert(
            "error",
            "Error",
            "Por favor selecciona un proveedor"
          );
          return false;
        }
        if (!formData.concepto_salida_id) {
          showCustomAlert("error", "Error", "Por favor selecciona un concepto");
          return false;
        }
        return true;
      case 2:
        if (!formData.titulo.trim()) {
          showCustomAlert("error", "Error", "Por favor ingresa un título");
          return false;
        }
        return true;
      case 3:
        const montoNumerico = parseFormattedNumber(formData.monto_base);
        if (!montoNumerico || parseFloat(montoNumerico) <= 0) {
          showCustomAlert(
            "error",
            "Error",
            "Por favor ingresa un monto válido"
          );
          return false;
        }
        if (!formData.fecha_presupuesto) {
          showCustomAlert("error", "Error", "Por favor selecciona una fecha");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      showCustomAlert("error", "Error", "El archivo no puede superar los 10MB");
      return;
    }

    if (!["image/jpeg", "image/png", "application/pdf"].includes(file.type)) {
      showCustomAlert(
        "error",
        "Error",
        "Solo se permiten archivos JPG, PNG o PDF"
      );
      return;
    }

    setUploadingFile(true);
    try {
      const cleanFileName = file.name
        .replace(/[^a-zA-Z0-9._-]/g, "_")
        .replace(/\s+/g, "_")
        .replace(/_+/g, "_");

      const fileName = `presupuesto_${Date.now()}_${cleanFileName}`;
      const { data, error } = await supabase.storage
        .from("comprobantes")
        .upload(fileName, file);

      if (error) {
        console.error("Error subiendo archivo:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al subir archivo: " + error.message
        );
      } else {
        const { data: urlData } = supabase.storage
          .from("comprobantes")
          .getPublicUrl(fileName);

        setFormData((prev) => ({
          ...prev,
          comprobante_url: urlData.publicUrl,
          tiene_comprobante: true,
        }));
        showCustomAlert("success", "Éxito", "Archivo subido correctamente");
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
    setUploadingFile(false);
  };

  const handleSave = async () => {
    if (!validateStep(3)) return;

    setGuardando(true);
    try {
      const montoNumerico = parseFormattedNumber(formData.monto_base);

      const dataToSave = {
        constructora_id: constructora.id,
        proyecto_id: formData.proyecto_id,
        proveedor_id: formData.proveedor_id,
        concepto_salida_id: formData.concepto_salida_id,
        etapa_id: formData.etapa_id || null,
        subetapa_id: formData.subetapa_id || null,
        tipo_presupuesto: tipoPresupuesto,
        codigo: formData.codigo || null,
        titulo: formData.titulo.trim(),
        descripcion: formData.descripcion.trim() || null,
        monto_base: parseFloat(montoNumerico),
        moneda: formData.moneda,
        tipo_cambio: parseFloat(formData.tipo_cambio),
        fecha_presupuesto: formData.fecha_presupuesto,
        comprobante_url: formData.comprobante_url,
        tiene_comprobante: formData.tiene_comprobante,
        monto_real_proveedor: formData.monto_real_proveedor ? parseFloat(parseFormattedNumber(formData.monto_real_proveedor)) : null,
        tiene_doble_monto: !!formData.tiene_doble_monto,
      };

      let result;
      if (presupuesto) {
        // Actualizar
        result = await supabase
          .from("presupuestos")
          .update(dataToSave)
          .eq("id", presupuesto.id)
          .select();
      } else {
        // Crear
        result = await supabase
          .from("presupuestos")
          .insert([dataToSave])
          .select();
      }

      const { data, error } = result;

      if (error) {
        console.error("Error guardando presupuesto:", error);
        showCustomAlert("error", "Error", "Error al guardar: " + error.message);
      } else {
        showCustomAlert(
          "success",
          "Éxito",
          presupuesto
            ? "Presupuesto actualizado correctamente"
            : "Presupuesto creado correctamente"
        );
        onSave();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
    setGuardando(false);
  };

  const getStepTitle = (step) => {
    switch (step) {
      case 1:
        return "Proyecto y Proveedor";
      case 2:
        return "Información del Presupuesto";
      case 3:
        return "Monto y Fecha";
      case 4:
        return "Resumen y Confirmación";
      default:
        return "";
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Proyecto */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Proyecto *
              </label>
              <CustomDropdown
                value={formData.proyecto_id}
                onChange={(value) =>
                  setFormData({ ...formData, proyecto_id: value })
                }
                options={proyectos.map((proyecto) => ({
                  value: proyecto.id,
                  label: `${proyecto.codigo} - ${proyecto.apellido_cliente}`,
                }))}
                placeholder="Selecciona un proyecto"
                icon={Building2}
              />
            </div>

            {/* Proveedor */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Proveedor *
              </label>
              <CustomDropdown
                value={formData.proveedor_id}
                onChange={(value) =>
                  setFormData({ ...formData, proveedor_id: value })
                }
                options={proveedores.map((proveedor) => ({
                  value: proveedor.id,
                  label: `${proveedor.nombre}${
                    proveedor.cuit ? ` - CUIT: ${proveedor.cuit}` : ""
                  }`,
                }))}
                placeholder="Selecciona un proveedor"
                icon={User}
              />
            </div>

            {/* Concepto */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Concepto de Gasto *
              </label>
              <CustomDropdown
                value={formData.concepto_salida_id}
                onChange={(value) =>
                  setFormData({ ...formData, concepto_salida_id: value })
                }
                options={conceptosSalida.map((concepto) => ({
                  value: concepto.id,
                  label: concepto.nombre,
                }))}
                placeholder="Selecciona un concepto"
                icon={Package}
              />
            </div>

            {/* Etapa */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Etapa (Opcional)
              </label>
              <CustomDropdown
                value={formData.etapa_id}
                onChange={(value) =>
                  setFormData({ ...formData, etapa_id: value })
                }
                options={[
                  { value: "", label: "Sin etapa específica" },
                  ...etapas.map((etapa) => ({
                    value: etapa.id,
                    label: etapa.nombre,
                  })),
                ]}
                placeholder="Selecciona una etapa"
                icon={Layers}
              />
            </div>

            {/* Subetapa */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Subetapa (Opcional)
              </label>
              <CustomDropdown
                value={formData.subetapa_id ? String(formData.subetapa_id) : ""}
                onChange={(value) =>
                  setFormData({ ...formData, subetapa_id: value })
                }
                options={[
                  {
                    value: "",
                    label: formData.etapa_id
                      ? "Sin subetapa específica"
                      : "Primero selecciona una etapa",
                  },
                  ...[
                    ...subetapasFiltradas,
                    // Si la subetapa seleccionada no está en el filtro, agregarla temporalmente
                    ...((formData.subetapa_id && !subetapasFiltradas.find((s) => String(s.id) === String(formData.subetapa_id)))
                      ? subetapas.filter((s) => String(s.id) === String(formData.subetapa_id))
                      : []),
                  ].map((subetapa) => {
                    const tienePresupuestoInicial = verificarPresupuestoInicial(
                      formData.proyecto_id,
                      formData.concepto_salida_id,
                      formData.etapa_id,
                      subetapa.id
                    );
                    return {
                      value: String(subetapa.id),
                      label: subetapa.nombre,
                      hasPresupuestoInicial: tienePresupuestoInicial,
                    };
                  }),
                ]}
                placeholder="Selecciona una subetapa"
                disabled={!formData.etapa_id}
                icon={Target}
              />
              {!formData.etapa_id && (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    margin: "4px 0 0 0",
                    fontStyle: "italic",
                  }}
                >
                  Primero selecciona una etapa
                </p>
              )}
              {/* Indicador de presupuesto inicial */}
              {formData.proyecto_id &&
                formData.concepto_salida_id &&
                formData.etapa_id &&
                formData.subetapa_id &&
                verificarPresupuestoInicial(
                  formData.proyecto_id,
                  formData.concepto_salida_id,
                  formData.etapa_id,
                  formData.subetapa_id
                ) && (
                  <div
                    style={{
                      marginTop: "8px",
                      padding: "10px 12px",
                      background:
                        "linear-gradient(135deg, #f3e8ff 0%, #fdf2f8 100%)",
                      border: "1px solid #d8b4fe",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "24px",
                        height: "24px",
                        background:
                          "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Target size={12} color="white" />
                    </div>
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#6b21a8",
                        margin: 0,
                        fontWeight: "600",
                        lineHeight: "1.3",
                      }}
                    >
                      Esta combinación tiene un presupuesto inicial registrado
                    </p>
                  </div>
                )}
            </div>
          </div>
        );

      case 2:
        return (
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Código generado automáticamente */}
            {formData.codigo && (
              <div
                style={{
                  background: "#f0f9ff",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: "1px solid #0ea5e9",
                }}
              >
                <p
                  style={{
                    margin: "0 0 4px 0",
                    fontSize: "12px",
                    color: "#0369a1",
                    fontWeight: "500",
                  }}
                >
                  Código generado automáticamente
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#0369a1",
                  }}
                >
                  {formData.codigo}
                </p>
              </div>
            )}

            {/* Título */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Título del presupuesto *
              </label>
              <input
                type="text"
                value={formData.titulo}
                onChange={(e) =>
                  setFormData({ ...formData, titulo: e.target.value })
                }
                placeholder="Ej: Materiales para fundación"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#2563eb";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(37, 99, 235, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            {/* Descripción */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Descripción (Opcional)
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                placeholder="Descripción detallada del presupuesto..."
                rows="4"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  resize: "vertical",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#2563eb";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(37, 99, 235, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Monto cliente y botón para costo real proveedor */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Monto cliente *
                </label>
                <input
                  type="text"
                  value={formData.monto_base}
                  onChange={(e) => {
                    const formatted = formatNumber(e.target.value);
                    setFormData({ ...formData, monto_base: formatted });
                  }}
                  placeholder="0,00"
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#2563eb";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(37, 99, 235, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                  }}
                />
                <p
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    margin: "4px 0 0 0",
                    fontStyle: "italic",
                  }}
                >
                  Use punto para miles y coma para decimales
                </p>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Moneda *
                </label>
                <CustomDropdown
                  value={formData.moneda}
                  onChange={(value) =>
                    setFormData({ ...formData, moneda: value })
                  }
                  options={[
                    { value: "ARS", label: "ARS (Pesos)" },
                    { value: "USD", label: "USD (Dólares)" },
                  ]}
                  placeholder="Selecciona moneda"
                  icon={DollarSign}
                />
                {/* Solo mostrar toggle de comisión para gastos generales, no para mano de obra */}
                {tipoPresupuesto !== "mano_obra" && (
                  <div style={{ marginTop: "8px" }}>
                    <button
                      type="button"
                      className="button-hover"
                      onClick={() => setFormData({ ...formData, tiene_doble_monto: !formData.tiene_doble_monto })}
                      style={{
                        fontSize: "12px",
                        padding: "8px 12px",
                        borderRadius: "10px",
                        border: formData.tiene_doble_monto ? "1px solid #8b5cf6" : "1px solid #e2e8f0",
                        background: formData.tiene_doble_monto ? "#f5f3ff" : "white",
                        color: formData.tiene_doble_monto ? "#6d28d9" : "#374151",
                      }}
                    >
                      {formData.tiene_doble_monto ? "Presupuesto con comisión (activo)" : "Presupuesto con comisión"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Solo mostrar monto real proveedor para gastos generales con comisión, no para mano de obra */}
            {formData.tiene_doble_monto && tipoPresupuesto !== "mano_obra" && (
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Monto Real Proveedor
                </label>
                <input
                  type="text"
                  value={formData.monto_real_proveedor}
                  onChange={(e) => {
                    const formatted = formatNumber(e.target.value);
                    setFormData({ ...formData, monto_real_proveedor: formatted });
                  }}
                  placeholder="0,00"
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#2563eb";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(37, 99, 235, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                  }}
                />
                {/* Diferencia en tiempo real */}
                {formData.monto_real_proveedor && (
                  <div
                    style={{
                      marginTop: 8,
                      background: "#eef2ff",
                      border: "1px solid #c7d2fe",
                      borderRadius: 10,
                      padding: "8px 12px",
                      color: "#3730a3",
                      fontSize: 12,
                      display: "grid",
                      gap: 4,
                    }}
                  >
                    {(() => {
                      const clienteARS = calcularARS(parseFormattedNumber(formData.monto_base), formData.moneda, formData.tipo_cambio);
                      const proveedorARS = calcularARS(parseFormattedNumber(formData.monto_real_proveedor || "0"), formData.moneda, formData.tipo_cambio);
                      const difARS = Math.max(clienteARS - proveedorARS, 0);
                      const tc = parseFloat(formData.tipo_cambio || 1) || 1;
                      const difUSD = tc > 0 ? (difARS / tc) : 0;
                      return (
                        <>
                          <span><strong>Comisión estimada (ARS):</strong> ${difARS.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          <span><strong>≈ USD:</strong> {difUSD.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Equivalencia */}
            {formData.monto_base && (
              <div
                style={{
                  background: "#f0f9ff",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: "1px solid #0ea5e9",
                }}
              >
                <p
                  style={{
                    margin: "0 0 4px 0",
                    fontSize: "12px",
                    color: "#0369a1",
                    fontWeight: "500",
                  }}
                >
                  Equivalencia al tipo de cambio actual
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#0369a1",
                  }}
                >
                  {calcularEquivalencia(
                    parseFormattedNumber(formData.monto_base),
                    formData.moneda,
                    formData.tipo_cambio
                  )}
                </p>
              </div>
            )}

            {/* Tipo de cambio */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Tipo de cambio (Dólar blue) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.tipo_cambio}
                onChange={(e) =>
                  setFormData({ ...formData, tipo_cambio: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#2563eb";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(37, 99, 235, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
              {dolarBlue && (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#059669",
                    margin: "4px 0 0 0",
                    fontWeight: "500",
                  }}
                >
                  <DollarSign
                    size={12}
                    style={{ verticalAlign: "text-bottom", marginRight: "4px" }}
                  />
                  Dólar blue actual: ${dolarBlue.toLocaleString()}
                </p>
              )}
            </div>

            {/* Fecha */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Fecha del presupuesto *
              </label>
              <input
                type="date"
                value={formData.fecha_presupuesto}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    fecha_presupuesto: e.target.value,
                  })
                }
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#2563eb";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(37, 99, 235, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            {/* Comprobante */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Comprobante (Opcional)
              </label>
              {formData.comprobante_url ? (
                <div
                  style={{
                    background: "#f0fdf4",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: "1px solid #bbf7d0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <CheckCircle2 size={16} color="#16a34a" />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#166534",
                        fontWeight: "500",
                      }}
                    >
                      Archivo subido correctamente
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        comprobante_url: null,
                        tiene_comprobante: false,
                      })
                    }
                    style={{
                      background: "none",
                      border: "none",
                      color: "#dc2626",
                      cursor: "pointer",
                      padding: "4px",
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div
                  style={{
                    border: "2px dashed #e2e8f0",
                    borderRadius: "12px",
                    padding: "20px",
                    textAlign: "center",
                    background: "#f8fafc",
                  }}
                >
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(e) => handleFileUpload(e.target.files[0])}
                    style={{ display: "none" }}
                    id="file-upload"
                    disabled={uploadingFile}
                  />
                  <label
                    htmlFor="file-upload"
                    style={{
                      cursor: uploadingFile ? "not-allowed" : "pointer",
                      display: "block",
                    }}
                  >
                    <Upload
                      size={24}
                      color="#64748b"
                      style={{ marginBottom: "8px" }}
                    />
                    <p
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: "14px",
                        color: "#374151",
                        fontWeight: "500",
                      }}
                    >
                      {uploadingFile
                        ? "Subiendo archivo..."
                        : "Haz clic para subir archivo"}
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "12px",
                        color: "#64748b",
                      }}
                    >
                      JPG, PNG o PDF (máx. 10MB)
                    </p>
                  </label>
                  {uploadingFile && (
                    <div
                      style={{
                        marginTop: "12px",
                        display: "inline-block",
                        width: "20px",
                        height: "20px",
                        border: "2px solid #e2e8f0",
                        borderTop: "2px solid #2563eb",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    ></div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        const proyecto = proyectos.find((p) => p.id === formData.proyecto_id);
        const proveedor = proveedores.find(
          (p) => p.id === formData.proveedor_id
        );
        const concepto = conceptosSalida.find(
          (c) => c.id === formData.concepto_salida_id
        );
        const etapa = etapas.find((e) => e.id === formData.etapa_id);
        const subetapa = subetapas.find((s) => s.id === formData.subetapa_id);

        return (
          <div style={{ display: "grid", gap: "20px" }}>
            <div
              style={{
                background: "#f8fafc",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={16} />
                Resumen del Presupuesto
              </h3>

              <div style={{ display: "grid", gap: "12px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Proyecto:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "600",
                    }}
                  >
                    {proyecto?.codigo} - {proyecto?.apellido_cliente}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Proveedor:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "600",
                    }}
                  >
                    {proveedor?.nombre}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Concepto:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "600",
                    }}
                  >
                    {concepto?.nombre}
                  </span>
                </div>

                {etapa && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "120px 1fr",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Etapa:
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#7c3aed",
                        fontWeight: "600",
                      }}
                    >
                      {etapa.nombre}
                    </span>
                  </div>
                )}

                {subetapa && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "120px 1fr",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Subetapa:
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#7c3aed",
                        fontWeight: "600",
                      }}
                    >
                      {subetapa.nombre}
                    </span>
                  </div>
                )}

                {formData.codigo && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "120px 1fr",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Código:
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#2563eb",
                        fontWeight: "600",
                      }}
                    >
                      {formData.codigo}
                    </span>
                  </div>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Título:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "600",
                    }}
                  >
                    {formData.titulo}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Monto:
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#059669",
                      fontWeight: "700",
                    }}
                  >
                    {formatearMoneda(
                      parseFormattedNumber(formData.monto_base),
                      formData.moneda
                    )}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Equivalencia:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    {calcularEquivalencia(
                      parseFormattedNumber(formData.monto_base),
                      formData.moneda,
                      formData.tipo_cambio
                    )}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Fecha:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "600",
                    }}
                  >
                    {formatDateForDisplay(formData.fecha_presupuesto)}
                  </span>
                </div>

                {formData.comprobante_url && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "120px 1fr",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Comprobante:
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#059669",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <CheckCircle2 size={14} />
                      Archivo adjunto
                    </span>
                  </div>
                )}
              </div>

              {/* Resumen financiero - solo si tiene doble monto */}
              {formData.tiene_doble_monto && (
                <div style={{ display: "grid", gap: "12px", marginTop: "12px" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                      gap: "12px",
                    }}
                  >
                    {/* Cliente */}
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>Monto cliente (ARS)</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>
                        {(() => {
                          const v = calcularARS(parseFormattedNumber(formData.monto_base), formData.moneda, formData.tipo_cambio);
                          return v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                      </div>
                    </div>
                    {/* Proveedor */}
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>Monto real proveedor (ARS)</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>
                        {(() => {
                          const v = calcularARS(parseFormattedNumber(formData.monto_real_proveedor || "0"), formData.moneda, formData.tipo_cambio);
                          return v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                      </div>
                    </div>
                    {/* Margen estimado */}
                    <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 12 }}>
                      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>Margen estimado (ARS)</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#16a34a" }}>
                        {(() => {
                          const cliente = calcularARS(parseFormattedNumber(formData.monto_base), formData.moneda, formData.tipo_cambio);
                          const prov = calcularARS(parseFormattedNumber(formData.monto_real_proveedor || "0"), formData.moneda, formData.tipo_cambio);
                          const v = Math.max(cliente - prov, 0);
                          return v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        })()}
                      </div>
                    </div>
                  </div>


                </div>
              )}
            </div>

            {formData.descripcion && (
              <div
                style={{
                  background: "#f8fafc",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  Descripción
                </h4>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#64748b",
                    lineHeight: "1.5",
                  }}
                >
                  {formData.descripcion}
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Log global en cada render del modal para depuración
  useEffect(() => {
    console.log("[DEBUG Modal] currentStep:", currentStep);
    console.log("[DEBUG Modal] formData.subetapa_id:", formData.subetapa_id);
    console.log("[DEBUG Modal] subetapasFiltradas:", subetapasFiltradas.map(s => s.id));
    console.log("[DEBUG Modal] subetapas todas:", subetapas.map(s => s.id));
  }, [currentStep, formData.subetapa_id, subetapasFiltradas, subetapas]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "40px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
            color: "white",
            padding: isMobile ? "20px" : "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: "0 0 4px 0",
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "700",
              }}
            >
              <Receipt
                size={20}
                style={{ verticalAlign: "text-bottom", marginRight: "8px" }}
              />
              {presupuesto ? "Editar presupuesto" : "Nuevo presupuesto"}
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                opacity: 0.9,
              }}
            >
              Paso {currentStep} de 4: {getStepTitle(currentStep)}
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "8px",
              padding: "8px",
              color: "white",
              cursor: "pointer",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.2)")
            }
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress bar */}
        <div
          style={{
            background: "#f8fafc",
            padding: "16px 24px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                style={{
                  flex: 1,
                  height: "4px",
                  borderRadius: "2px",
                  background:
                    step <= currentStep
                      ? getVistaColor("presupuestos").solid
                      : "#e2e8f0",
                  transition: "background 0.3s ease",
                }}
              />
            ))}
          </div>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "24px",
            overflowY: "auto",
          }}
        >
          {renderStep()}
        </div>

        {/* Footer */}
        <div
          style={{
            background: "#f8fafc",
            padding: isMobile ? "16px 20px" : "20px 24px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <button
            onClick={currentStep === 1 ? onClose : handlePrev}
            style={{
              padding: "12px 20px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            className="button-hover"
          >
            {currentStep === 1 ? "Cancelar" : "Anterior"}
          </button>

          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              className="button-hover"
            >
              Siguiente
              <ChevronDown size={16} style={{ transform: "rotate(-90deg)" }} />
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={guardando}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                background: guardando
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: guardando ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              className={!guardando ? "button-hover" : ""}
            >
              {guardando ? (
                <>
                  <Loader2 size={16} className="loading-pulse" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  {presupuesto ? "Actualizar" : "Crear"} Presupuesto
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Modal para vista detallada del presupuesto
function PresupuestoDetailModal({
  presupuesto,
  adicionales,
  pagosDetectados,
  onClose,
  onEditAdicional,
  onAddAdicional,
  onReloadPresupuesto,
  onReloadAllData,
  formatearMoneda,
  calcularEquivalencia,
  isMobile,
  permisosCliente,
  showCustomAlert,
}) {
  const saldoData = presupuesto.saldo_data || {};
  const total = parseFloat(
    saldoData.total_presupuesto || presupuesto.monto_base || 0
  );
  const totalPagado = parseFloat(saldoData.total_pagado || 0);
  const saldoPendiente = Math.max(0, total - totalPagado);
  const porcentajePagado = total > 0 ? (totalPagado / total) * 100 : 0;

  const handleDeleteAdicional = async (adicionalId) => {
    if (permisosCliente.esCliente) {
      showCustomAlert(
        "error",
        "Acción No Permitida",
        "No tienes permisos para eliminar adicionales."
      );
      return;
    }

    showCustomAlert(
      "confirm",
      "¿Eliminar adicional?",
      "¿Estás seguro de eliminar este adicional? Esta acción no se puede deshacer.",
      async () => {
        try {
          const { error } = await supabase
            .from("adicionales_presupuesto")
            .delete()
            .eq("id", adicionalId);

          if (error) {
            console.error("Error eliminando adicional:", error);
            showCustomAlert(
              "error",
              "Error",
              "Error al eliminar: " + error.message
            );
          } else {
            showCustomAlert(
              "success",
              "Éxito",
              "Adicional eliminado correctamente"
            );
            // Recargar el detalle del presupuesto específico
            onReloadPresupuesto(presupuesto);
            // También recargar todos los datos para actualizar gráficos
            setTimeout(() => {
              onReloadAllData();
            }, 500);
          }
        } catch (err) {
          console.error("Error:", err);
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      }
    );
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
            color: "white",
            padding: isMobile ? "20px" : "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: "0 0 4px 0",
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "700",
              }}
            >
              <Eye
                size={20}
                style={{ verticalAlign: "text-bottom", marginRight: "8px" }}
              />
              Vista Detallada - Presupuesto
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                opacity: 0.9,
              }}
            >
              {presupuesto.codigo || presupuesto.titulo}
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "8px",
              padding: "8px",
              color: "white",
              cursor: "pointer",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.2)")
            }
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "24px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
              gap: "24px",
            }}
          >
            {/* Información Principal */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={16} />
                Información Principal
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Proyecto */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Proyecto
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    <Building2
                      size={16}
                      style={{
                        verticalAlign: "text-bottom",
                        marginRight: "6px",
                      }}
                    />
                    {presupuesto.proyectos?.codigo}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  >
                    Cliente: {presupuesto.proyectos?.apellido_cliente}
                  </p>
                </div>

                {/* Proveedor */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Proveedor
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    <User
                      size={16}
                      style={{
                        verticalAlign: "text-bottom",
                        marginRight: "6px",
                      }}
                    />
                    {presupuesto.proveedores_servicios?.nombre}
                  </p>
                  {presupuesto.proveedores_servicios?.cuit && (
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "13px",
                        color: "#64748b",
                      }}
                    >
                      CUIT: {presupuesto.proveedores_servicios.cuit}
                    </p>
                  )}
                </div>

                {/* Concepto */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Concepto de Gasto
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    <Package
                      size={16}
                      style={{
                        verticalAlign: "text-bottom",
                        marginRight: "6px",
                      }}
                    />
                    {presupuesto.conceptos_salida?.nombre}
                  </p>
                </div>

                {/* Etapa y Subetapa - NUEVO */}
                {(presupuesto.etapas_obra || presupuesto.subetapas_obra) && (
                  <div
                    style={{
                      background: "#f3e8ff",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "1px solid #e9d5ff",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 12px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#7c3aed",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Layers size={12} />
                      Etapa de Obra
                    </p>

                    {presupuesto.etapas_obra && (
                      <div
                        style={{
                          marginBottom: presupuesto.subetapas_obra
                            ? "12px"
                            : "0",
                        }}
                      >
                        <p
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "11px",
                            fontWeight: "600",
                            color: "#6b21a8",
                            textTransform: "uppercase",
                          }}
                        >
                          Etapa
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#7c3aed",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Layers size={14} />
                          {presupuesto.etapas_obra.nombre}
                        </p>
                      </div>
                    )}

                    {presupuesto.subetapas_obra && (
                      <div>
                        <p
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "11px",
                            fontWeight: "600",
                            color: "#6b21a8",
                            textTransform: "uppercase",
                          }}
                        >
                          Subetapa
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#7c3aed",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Target size={14} />
                          {presupuesto.subetapas_obra.nombre}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Descripción */}
                {presupuesto.descripcion && (
                  <div
                    style={{
                      background: "#f8fafc",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Descripción
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#1e293b",
                        lineHeight: "1.5",
                      }}
                    >
                      {presupuesto.descripcion}
                    </p>
                  </div>
                )}

                {/* Comprobante */}
                {presupuesto.comprobante_url && (
                  <div
                    style={{
                      background: "#f0fdf4",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#166534",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Comprobante
                    </p>
                    <a
                      href={presupuesto.comprobante_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        color: "#166534",
                        textDecoration: "none",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      <FileText size={16} />
                      Ver comprobante
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Resumen Financiero */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <BarChart3 size={16} />
                Resumen Financiero
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Monto Total */}
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                    padding: "20px",
                    borderRadius: "12px",
                    border: "2px solid #bbf7d0",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#166534",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Monto Total del Presupuesto
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: isMobile ? "20px" : "24px",
                      fontWeight: "800",
                      color: "#059669",
                    }}
                  >
                    {formatearMoneda(total, presupuesto.moneda)}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "12px",
                      color: "#166534",
                    }}
                  >
                    {calcularEquivalencia(
                      total,
                      presupuesto.moneda,
                      presupuesto.tipo_cambio
                    )}
                  </p>
                </div>

                {/* Progreso de Pagos */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Progreso de Pagos
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        color: porcentajePagado >= 100 ? "#059669" : "#f59e0b",
                      }}
                    >
                      {porcentajePagado.toFixed(1)}%
                    </span>
                  </div>

                  {/* Barra de progreso */}
                  <div
                    style={{
                      background: "#e2e8f0",
                      height: "8px",
                      borderRadius: "4px",
                      overflow: "hidden",
                      marginBottom: "12px",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        background:
                          porcentajePagado >= 100
                            ? "linear-gradient(135deg, #059669 0%, #16a34a 100%)"
                            : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        width: `${Math.min(porcentajePagado, 100)}%`,
                        transition: "width 0.3s ease",
                      }}
                    ></div>
                  </div>

                  <div style={{ display: "grid", gap: "8px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: "13px", color: "#64748b" }}>
                        Pagado:
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#059669",
                          fontWeight: "600",
                        }}
                      >
                        {formatearMoneda(totalPagado, presupuesto.moneda)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: "13px", color: "#64748b" }}>
                        Pendiente:
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          color: saldoPendiente > 0 ? "#dc2626" : "#059669",
                          fontWeight: "600",
                        }}
                      >
                        {formatearMoneda(saldoPendiente, presupuesto.moneda)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Información adicional */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{ display: "grid", gap: "8px", fontSize: "13px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: "#64748b" }}>
                        <Calendar
                          size={12}
                          style={{
                            verticalAlign: "text-bottom",
                            marginRight: "4px",
                          }}
                        />
                        Fecha:
                      </span>
                      <span style={{ fontWeight: "600", color: "#1e293b" }}>
                        {formatDateForDisplay(presupuesto.fecha_presupuesto)}
                      </span>
                    </div>

                    {presupuesto.tipo_cambio &&
                      parseFloat(presupuesto.tipo_cambio) !== 1 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span style={{ color: "#64748b" }}>
                            <DollarSign
                              size={12}
                              style={{
                                verticalAlign: "text-bottom",
                                marginRight: "4px",
                              }}
                            />
                            Tipo de cambio:
                          </span>
                          <span style={{ fontWeight: "600", color: "#1e293b" }}>
                            ${presupuesto.tipo_cambio}
                          </span>
                        </div>
                      )}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ color: "#64748b" }}>
                        <Clock
                          size={12}
                          style={{
                            verticalAlign: "text-bottom",
                            marginRight: "4px",
                          }}
                        />
                        Creado:
                      </span>
                      <span style={{ fontWeight: "600", color: "#1e293b" }}>
                        {new Date(presupuesto.created_at).toLocaleDateString(
                          "es-ES"
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Adicionales */}
          <div style={{ marginTop: "32px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Plus size={16} />
                Adicionales del Presupuesto ({adicionales.length})
              </h3>

              {/* Solo mostrar botón si no es cliente */}
              {!permisosCliente.esCliente && (
                <button
                  onClick={() => onAddAdicional(presupuesto)}
                  style={{
                    background:
                      "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.2s ease",
                  }}
                  className="button-hover"
                >
                  <Plus size={14} />
                  Agregar Adicional
                </button>
              )}
            </div>

            {adicionales.length === 0 ? (
              <div
                style={{
                  background: "#f8fafc",
                  padding: "32px 20px",
                  borderRadius: "12px",
                  border: "2px dashed #cbd5e1",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    background: "#e2e8f0",
                    borderRadius: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                  }}
                >
                  <Plus size={24} color="#64748b" />
                </div>
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  No hay adicionales
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#64748b",
                  }}
                >
                  {permisosCliente.esCliente
                    ? "Este presupuesto no tiene adicionales registrados"
                    : "Agrega adicionales para modificar el monto del presupuesto"}
                </p>
              </div>
            ) : (
              <div
                style={{
                  background: "white",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #e2e8f0",
                }}
              >
                {adicionales.map((adicional, index) => (
                  <div
                    key={adicional.id}
                    style={{
                      padding: "16px 20px",
                      borderBottom:
                        index < adicionales.length - 1
                          ? "1px solid #f1f5f9"
                          : "none",
                      transition: "background 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isMobile) e.target.style.backgroundColor = "#f8fafc";
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile)
                        e.target.style.backgroundColor = "transparent";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <h4
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1e293b",
                          }}
                        >
                          {adicional.titulo}
                        </h4>
                        {adicional.descripcion && (
                          <p
                            style={{
                              margin: "0 0 8px 0",
                              fontSize: "13px",
                              color: "#64748b",
                              lineHeight: "1.4",
                            }}
                          >
                            {adicional.descripcion}
                          </p>
                        )}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          <span>
                            <Calendar
                              size={10}
                              style={{
                                verticalAlign: "text-bottom",
                                marginRight: "2px",
                              }}
                            />
                            {formatDateForDisplay(adicional.fecha_adicional)}
                          </span>
                          <span>
                            <Clock
                              size={10}
                              style={{
                                verticalAlign: "text-bottom",
                                marginRight: "2px",
                              }}
                            />
                            {new Date(adicional.created_at).toLocaleDateString(
                              "es-ES"
                            )}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          textAlign: "right",
                          marginLeft: "16px",
                        }}
                      >
                        <p
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "16px",
                            fontWeight: "700",
                            color:
                              parseFloat(adicional.monto) >= 0
                                ? "#059669"
                                : "#dc2626",
                          }}
                        >
                          {parseFloat(adicional.monto) >= 0 ? "+" : ""}
                          {formatearMoneda(adicional.monto, presupuesto.moneda)}
                        </p>

                        {/* Solo mostrar botones de acción si no es cliente */}
                        {!permisosCliente.esCliente && (
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              justifyContent: "flex-end",
                              marginTop: "8px",
                            }}
                          >
                            <button
                              onClick={() => onEditAdicional(adicional)}
                              style={{
                                background: "#f1f5f9",
                                border: "none",
                                borderRadius: "4px",
                                padding: "4px 6px",
                                cursor: "pointer",
                                color: "#475569",
                                fontSize: "11px",
                                transition: "background 0.2s ease",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background = "#e2e8f0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "#f1f5f9")
                              }
                            >
                              <Edit size={12} />
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteAdicional(adicional.id)
                              }
                              style={{
                                background: "#fef2f2",
                                border: "none",
                                borderRadius: "4px",
                                padding: "4px 6px",
                                cursor: "pointer",
                                color: "#dc2626",
                                fontSize: "11px",
                                transition: "background 0.2s ease",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background = "#fee2e2")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "#fef2f2")
                              }
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagos Detectados */}
          <div style={{ marginTop: "32px" }}>
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <TrendingDown size={16} />
              Pagos Relacionados ({pagosDetectados.length})
            </h3>

            {pagosDetectados.length === 0 ? (
              <div
                style={{
                  background: "#f8fafc",
                  padding: "32px 20px",
                  borderRadius: "12px",
                  border: "2px dashed #cbd5e1",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    background: "#e2e8f0",
                    borderRadius: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                  }}
                >
                  <TrendingDown size={24} color="#64748b" />
                </div>
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  No hay pagos registrados
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#64748b",
                  }}
                >
                  Los pagos relacionados aparecerán automáticamente cuando se
                  registren salidas vinculadas a este presupuesto
                </p>
              </div>
            ) : (
              <div
                style={{
                  background: "white",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  border: "1px solid #e2e8f0",
                }}
              >
                {pagosDetectados.map((pago, index) => (
                  <div
                    key={pago.id || index}
                    style={{
                      padding: "16px 20px",
                      borderBottom:
                        index < pagosDetectados.length - 1
                          ? "1px solid #f1f5f9"
                          : "none",
                      transition: "background 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isMobile) e.target.style.backgroundColor = "#f8fafc";
                    }}
                    onMouseLeave={(e) => {
                      if (!isMobile)
                        e.target.style.backgroundColor = "transparent";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <h4
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1e293b",
                          }}
                        >
                          {pago.concepto || "Pago relacionado"}
                        </h4>
                        {pago.descripcion && (
                          <p
                            style={{
                              margin: "0 0 8px 0",
                              fontSize: "13px",
                              color: "#64748b",
                              lineHeight: "1.4",
                            }}
                          >
                            {pago.descripcion}
                          </p>
                        )}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          <span>
                            <Calendar
                              size={10}
                              style={{
                                verticalAlign: "text-bottom",
                                marginRight: "2px",
                              }}
                            />
                            {formatDateForDisplay(pago.fecha)}
                          </span>
                          {pago.caja_nombre && (
                            <span>
                              <Wallet
                                size={10}
                                style={{
                                  verticalAlign: "text-bottom",
                                  marginRight: "2px",
                                }}
                              />
                              {pago.caja_nombre}
                            </span>
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          textAlign: "right",
                          marginLeft: "16px",
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#dc2626",
                          }}
                        >
                          -{formatearMoneda(pago.monto, pago.moneda)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal para crear/editar adicionales
function AdicionalModal({
  presupuesto,
  adicional,
  onClose,
  onSave,
  formatearMoneda,
  isMobile,
  showCustomAlert,
}) {
  const [formData, setFormData] = useState({
    titulo: "",
    descripcion: "",
    monto: "",
    monto_real_proveedor: "",
    fecha_adicional: new Date().toISOString().split("T")[0],
    tipo_adicional: "otro",
    concepto: "",
  });
  const [guardando, setGuardando] = useState(false);
  const [mostrarDobleMonto, setMostrarDobleMonto] = useState(false);

  useEffect(() => {
    if (adicional) {
      // Modo edición
      setFormData({
        titulo: adicional.titulo,
        descripcion: adicional.descripcion || "",
        monto: formatNumber(adicional.monto.toString()),
        monto_real_proveedor: formatNumber(adicional.monto_real_proveedor?.toString() || adicional.monto.toString()),
        fecha_adicional: formatDateForInput(adicional.fecha_adicional),
        tipo_adicional: adicional.tipo_adicional || "otro",
        concepto: adicional.concepto || "",
      });
      // Si el adicional tiene monto_real_proveedor diferente al monto, mostrar doble monto
      setMostrarDobleMonto(adicional.monto_real_proveedor !== null && adicional.monto_real_proveedor !== undefined && adicional.monto_real_proveedor !== adicional.monto);
    } else {
      // Modo creación - resetear estado
      setMostrarDobleMonto(false);
    }
  }, [adicional]);

  const handleSave = async () => {
    // Validaciones
    if (!formData.titulo.trim()) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor ingresa un título para el adicional"
      );
      return;
    }

    if (!formData.concepto.trim()) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor ingresa un concepto para el adicional"
      );
      return;
    }

    const montoNumerico = parseFormattedNumber(formData.monto);
    if (!montoNumerico || isNaN(parseFloat(montoNumerico))) {
      showCustomAlert("error", "Error", "Por favor ingresa un monto válido");
      return;
    }

    let montoRealProveedorNumerico = null;
    if (mostrarDobleMonto) {
      montoRealProveedorNumerico = parseFormattedNumber(formData.monto_real_proveedor);
      if (!montoRealProveedorNumerico || isNaN(parseFloat(montoRealProveedorNumerico))) {
        showCustomAlert("error", "Error", "Por favor ingresa un monto real proveedor válido");
        return;
      }
    }

    if (!formData.fecha_adicional) {
      showCustomAlert("error", "Error", "Por favor selecciona una fecha");
      return;
    }

    setGuardando(true);
    try {
      const dataToSave = {
        presupuesto_id: presupuesto.id,
        titulo: formData.titulo.trim(),
        concepto: formData.concepto.trim(),
        descripcion: formData.descripcion.trim() || null,
        monto: parseFloat(montoNumerico),
        fecha_adicional: formData.fecha_adicional,
        tipo_adicional: formData.tipo_adicional,
        destino_adicional: "ambos",
        aplica_cliente: true,
        aplica_proveedor: true,
      };

      // Agregar monto real proveedor solo si el toggle está activo
      if (mostrarDobleMonto && montoRealProveedorNumerico !== null) {
        dataToSave.monto_real_proveedor = parseFloat(montoRealProveedorNumerico);
      }

      let result;
      if (adicional) {
        // Actualizar
        result = await supabase
          .from("adicionales_presupuesto")
          .update(dataToSave)
          .eq("id", adicional.id)
          .select();
      } else {
        // Crear
        result = await supabase
          .from("adicionales_presupuesto")
          .insert([dataToSave])
          .select();
      }

      const { data, error } = result;

      if (error) {
        console.error("Error guardando adicional:", error);
        showCustomAlert("error", "Error", "Error al guardar: " + error.message);
      } else {
        showCustomAlert(
          "success",
          "Éxito",
          adicional
            ? "Adicional actualizado correctamente"
            : "Adicional creado correctamente"
        );
        onSave();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
    setGuardando(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "40px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
            color: "white",
            padding: isMobile ? "20px" : "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: "0 0 4px 0",
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "700",
              }}
            >
              <Plus
                size={20}
                style={{ verticalAlign: "text-bottom", marginRight: "8px" }}
              />
              {adicional ? "Editar Adicional" : "Nuevo Adicional"}
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                opacity: 0.9,
              }}
            >
              {presupuesto.titulo}
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "8px",
              padding: "8px",
              color: "white",
              cursor: "pointer",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.2)")
            }
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "24px",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Título */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Título del Adicional *
              </label>
              <input
                type="text"
                value={formData.titulo}
                onChange={(e) =>
                  setFormData({ ...formData, titulo: e.target.value })
                }
                placeholder="Ej: Materiales adicionales por cambio de diseño"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#10b981";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(16, 185, 129, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            {/* Concepto */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Concepto *
              </label>
              <input
                type="text"
                value={formData.concepto}
                onChange={(e) =>
                  setFormData({ ...formData, concepto: e.target.value })
                }
                placeholder="Ej: Materiales adicionales, Trabajo extra, etc."
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#10b981";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(16, 185, 129, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            {/* Tipo de Adicional */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Tipo de Adicional *
              </label>
              <select
                value={formData.tipo_adicional}
                onChange={(e) =>
                  setFormData({ ...formData, tipo_adicional: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                  background: "white",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#10b981";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(16, 185, 129, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              >
                <option value="trabajo_extra">Trabajo Extra</option>
                <option value="inflacion">Inflación</option>
                <option value="materiales">Materiales</option>
                <option value="otro">Otro</option>
              </select>
            </div>

            {/* Descripción */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Descripción (Opcional)
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                placeholder="Descripción detallada del adicional..."
                rows="3"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  resize: "vertical",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#10b981";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(16, 185, 129, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            {/* Toggle para doble monto */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                <div>
                  <p style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "600", color: "#374151" }}>
                    Tipo de Adicional
                  </p>
                  <p style={{ margin: "0", fontSize: "12px", color: "#64748b" }}>
                    {mostrarDobleMonto 
                      ? "Adicional con comisión: define montos diferentes para cliente y proveedor"
                      : "Adicional simple: el mismo monto se aplica a cliente y proveedor"
                    }
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setMostrarDobleMonto(!mostrarDobleMonto)}
                  style={{
                    fontSize: "12px",
                    padding: "8px 12px",
                    borderRadius: "10px",
                    border: mostrarDobleMonto ? "1px solid #8b5cf6" : "1px solid #e2e8f0",
                    background: mostrarDobleMonto ? "#f5f3ff" : "white",
                    color: mostrarDobleMonto ? "#6d28d9" : "#374151",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  {mostrarDobleMonto ? "Adicional con comisión (activo)" : "Adicional con comisión"}
                </button>
              </div>
            </div>

            {/* Campos de Monto - uno o dos según el toggle */}
            {mostrarDobleMonto ? (
              <>
                {/* Monto Cliente */}
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Monto cliente ({presupuesto.moneda}) *
                  </label>
                  <input
                    type="text"
                    value={formatNumber(formData.monto)}
                    onChange={(e) => {
                      const parsedValue = parseFormattedNumber(e.target.value);
                      setFormData({ ...formData, monto: parsedValue });
                    }}
                    placeholder="0,00 (positivo para suma, negativo para resta)"
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      border: "2px solid #f1f5f9",
                      borderRadius: "12px",
                      fontSize: "16px",
                      boxSizing: "border-box",
                      outline: "none",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      touchAction: "manipulation",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#10b981";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(16, 185, 129, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#f1f5f9";
                      e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                    }}
                  />
                </div>

                {/* Monto Real Proveedor */}
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Monto real proveedor ({presupuesto.moneda}) *
                  </label>
                  <input
                    type="text"
                    value={formatNumber(formData.monto_real_proveedor)}
                    onChange={(e) => {
                      const parsedValue = parseFormattedNumber(e.target.value);
                      setFormData({ ...formData, monto_real_proveedor: parsedValue });
                    }}
                    placeholder="0,00 (positivo para suma, negativo para resta)"
                    style={{
                      width: "100%",
                      padding: "14px 16px",
                      border: "2px solid #f1f5f9",
                      borderRadius: "12px",
                      fontSize: "16px",
                      boxSizing: "border-box",
                      outline: "none",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      touchAction: "manipulation",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#10b981";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(16, 185, 129, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#f1f5f9";
                      e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                    }}
                  />
                </div>
                
                <p
                  style={{
                    margin: "6px 0 0 0",
                    fontSize: "12px",
                    color: "#64748b",
                    lineHeight: "1.4",
                  }}
                >
                  Use punto para miles y coma para decimales. Ingresa valores
                  positivos para sumar o negativos para restar
                </p>
              </>
            ) : (
              /* Monto Simple para presupuestos normales */
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Monto del adicional ({presupuesto.moneda}) *
                </label>
                <input
                  type="text"
                  value={formatNumber(formData.monto)}
                  onChange={(e) => {
                    const parsedValue = parseFormattedNumber(e.target.value);
                    setFormData({ ...formData, monto: parsedValue });
                  }}
                  placeholder="0,00 (positivo para suma, negativo para resta)"
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#10b981";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(16, 185, 129, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                  }}
                />
                <p
                  style={{
                    margin: "6px 0 0 0",
                    fontSize: "12px",
                    color: "#64748b",
                    lineHeight: "1.4",
                  }}
                >
                  Use punto para miles y coma para decimales. Ingresa un valor
                  positivo para sumar o negativo para restar
                </p>
              </div>
            )}

            {/* Fecha */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Fecha del Adicional *
              </label>
              <input
                type="date"
                value={formData.fecha_adicional}
                onChange={(e) =>
                  setFormData({ ...formData, fecha_adicional: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#10b981";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(16, 185, 129, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>


            {/* Vista previa del monto */}
            {formData.monto && (
              <div
                style={{
                  background: "#f8fafc",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <p
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "14px",
                    color: "#374151",
                    fontWeight: "600",
                  }}
                >
                  Efecto en el presupuesto
                </p>
                
                <div style={{ display: "grid", gap: "8px" }}>
                  {/* Monto Cliente */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>Monto cliente:</span>
                    <span style={{ 
                      fontSize: "12px", 
                      fontWeight: "600",
                      color: parseFloat(parseFormattedNumber(formData.monto)) >= 0 ? "#059669" : "#dc2626"
                    }}>
                      {parseFloat(parseFormattedNumber(formData.monto)) >= 0 ? "+" : ""}
                      {formatearMoneda(parseFloat(parseFormattedNumber(formData.monto)), presupuesto.moneda)}
                    </span>
                  </div>

                  {/* Monto Proveedor - solo si está en modo doble monto */}
                  {mostrarDobleMonto && formData.monto_real_proveedor && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>Monto proveedor:</span>
                      <span style={{ 
                        fontSize: "12px", 
                        fontWeight: "600",
                        color: parseFloat(parseFormattedNumber(formData.monto_real_proveedor)) >= 0 ? "#0369a1" : "#dc2626"
                      }}>
                        {parseFloat(parseFormattedNumber(formData.monto_real_proveedor)) >= 0 ? "+" : ""}
                        {formatearMoneda(parseFloat(parseFormattedNumber(formData.monto_real_proveedor)), presupuesto.moneda)}
                      </span>
                    </div>
                  )}

                  {/* Comisión Constructora - solo si está en modo doble monto */}
                  {mostrarDobleMonto && formData.monto_real_proveedor && (
                    <div style={{ 
                      display: "flex", 
                      justifyContent: "space-between", 
                      alignItems: "center",
                      padding: "8px 12px",
                      background: "#fef3c7",
                      borderRadius: "8px",
                      border: "1px solid #f59e0b"
                    }}>
                      <span style={{ fontSize: "12px", color: "#92400e", fontWeight: "600" }}>Comisión Constructora:</span>
                      <span style={{ 
                        fontSize: "12px", 
                        fontWeight: "700",
                        color: "#92400e"
                      }}>
                        {(() => {
                          const montoCliente = parseFloat(parseFormattedNumber(formData.monto));
                          const montoProveedor = parseFloat(parseFormattedNumber(formData.monto_real_proveedor));
                          const diferencia = montoCliente - montoProveedor;
                          return diferencia >= 0 ? "+" : "";
                        })()}
                        {(() => {
                          const montoCliente = parseFloat(parseFormattedNumber(formData.monto));
                          const montoProveedor = parseFloat(parseFormattedNumber(formData.monto_real_proveedor));
                          const diferencia = montoCliente - montoProveedor;
                          return formatearMoneda(diferencia, presupuesto.moneda);
                        })()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            background: "#f8fafc",
            padding: isMobile ? "16px 20px" : "20px 24px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "12px 20px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            className="button-hover"
          >
            Cancelar
          </button>

          <button
            onClick={handleSave}
            disabled={
              guardando ||
              !formData.titulo.trim() ||
              !formData.monto ||
              !formData.fecha_adicional
            }
            style={{
              padding: "12px 24px",
              border: "none",
              borderRadius: "8px",
              background:
                guardando ||
                !formData.titulo.trim() ||
                !formData.monto ||
                !formData.fecha_adicional
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor:
                guardando ||
                !formData.titulo.trim() ||
                !formData.monto ||
                !formData.fecha_adicional
                  ? "not-allowed"
                  : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s ease",
            }}
            className={
              !guardando &&
              formData.titulo.trim() &&
              formData.monto &&
              formData.fecha_adicional
                ? "button-hover"
                : ""
            }
          >
            {guardando ? (
              <>
                <Loader2 size={16} className="loading-pulse" />
                Guardando...
              </>
            ) : (
              <>
                <Save size={16} />
                {adicional ? "Actualizar" : "Crear"} Adicional
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Componente principal de vista de mano de obra
function ManoObraView({ 
  datosManoObra, 
  proyectos, 
  proyectosExpandidos, 
  onToggleProyecto, 
  formatearMoneda, 
  isMobile,
  onEdit,
  onDelete,
  onDetail,
  onAddAdicional,
  calcularEquivalencia,
  getEstadoColor,
  getEstadoLabel,
  permisosCliente
}) {
  console.log("🔍 ManoObraView recibió datos:", datosManoObra);
  console.log("🔍 Cantidad de proyectos en ManoObraView:", datosManoObra.length);
  console.log("🔍 Proyectos disponibles:", proyectos);
  console.log("🔍 Cantidad de proyectos disponibles:", proyectos?.length);
  
  return (
    <div style={{ 
      display: "grid", 
      gap: "16px",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(400px, 1fr))",
      alignItems: "start",
      animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)"
    }}>
      {datosManoObra.map((datosProyecto) => {
        const proyecto = proyectos.find(p => p.id === datosProyecto.proyecto_id);
        console.log("🔍 Buscando proyecto:", datosProyecto.proyecto_id, "Encontrado:", proyecto?.nombre);
        if (!proyecto) {
          console.log("❌ Proyecto no encontrado para ID:", datosProyecto.proyecto_id);
          return null;
        }
        
        const { calculos, presupuestos } = datosProyecto;
        


        // Crear tarjeta con información real del proyecto (no simulada)
        const presupuestoProyecto = {
          id: `proyecto-${datosProyecto.proyecto_id}`,
          titulo: proyecto.nombre,
          descripcion: `${presupuestos.length} proveedor${presupuestos.length !== 1 ? 'es' : ''} de mano de obra`,
          proyectos: {
            codigo: proyecto.codigo,
            apellido_cliente: proyecto.apellido_cliente
          },
          proveedores_servicios: { nombre: proyecto.nombre },
          conceptos_salida: { nombre: "Mano de obra" },
          monto_base: calculos.totalCliente,
          monto_real_proveedor: calculos.totalPresupuestosReales,
          moneda: "ARS",
          tiene_doble_monto: true,
          datos_financieros: {
            totalClienteARS: calculos.totalCliente,
            pagadoClienteARS: calculos.disponibleEnCaja,
            saldoClienteARS: calculos.totalCliente - calculos.disponibleEnCaja,
            totalProveedorARS: calculos.totalPresupuestosReales,
            pagadoProveedorNetoARS: calculos.totalPagado,
            saldoProveedorARS: calculos.totalPresupuestosReales - calculos.totalPagado,
            comisionTotal: calculos.totalComision,
            // Valor real de comisión cobrada (MO): transferencias desde cajas MO del proyecto hacia cajas empresa
            comisionCobradaARS: calculos.totalCobrado,
            comisionPendiente: calculos.totalComision - calculos.totalCobrado,
            disponibleEnCaja: calculos.saldoRealCajasMO,
            porcentajeCliente: calculos.totalCliente > 0 ? (calculos.disponibleEnCaja / calculos.totalCliente) * 100 : 0,
            porcentajeProveedor: calculos.totalPresupuestosReales > 0 ? (calculos.totalPagado / calculos.totalPresupuestosReales) * 100 : 0,
            porcentajeComision: calculos.totalComision > 0 ? (calculos.totalCobrado / calculos.totalComision) * 100 : 0
          },
          saldo_data: {
            total_presupuesto: calculos.totalCliente,
            total_pagado: calculos.totalPagado,
            porcentaje_pagado: calculos.totalPresupuestosReales > 0 ? (calculos.totalPagado / calculos.totalPresupuestosReales) * 100 : 0,
            saldo_pendiente: calculos.totalPresupuestosReales - calculos.totalPagado
          }
        };

        console.log("🔍 Presupuesto proyecto creado:", presupuestoProyecto);
        console.log("🔍 Nombre del proyecto:", proyecto.nombre);
        console.log("🔍 Cantidad de presupuestos en este proyecto:", presupuestos.length);

        return (
          <div key={datosProyecto.proyecto_id}>
            <PresupuestoCard
              presupuesto={presupuestoProyecto}
              onEdit={() => {}}
              onDelete={() => {}}
              onRestore={() => {}}
              filterVigencia="activos"
              onDetail={() => {}}
              onAddAdicional={() => {}}
              formatearMoneda={formatearMoneda}
              calcularEquivalencia={() => ""}
              getEstadoColor={() => "#059669"}
              getEstadoLabel={() => "Activo"}
              isMobile={isMobile}
              permisosCliente={{ esCliente: false }}
              index={0}
              isProyectoMO={true}
              onToggleExpandir={() => onToggleProyecto(datosProyecto.proyecto_id)}
              isExpanded={proyectosExpandidos.has(datosProyecto.proyecto_id)}
            />

            {proyectosExpandidos.has(datosProyecto.proyecto_id) && (
              <div style={{ marginTop: "16px", paddingLeft: "16px" }}>
                <div style={{ display: "grid", gap: "16px" }}>
                  {presupuestos.map((presupuesto, index) => (
                    <PresupuestoCard
                      key={presupuesto.id}
                      presupuesto={{
                        ...presupuesto,
                        saldo_data: {
                          total_presupuesto: presupuesto.monto_base,
                          total_pagado: calculos.pagosPorPresupuesto[presupuesto.id] || 0,
                          porcentaje_pagado: presupuesto.monto_base > 0 ? ((calculos.pagosPorPresupuesto[presupuesto.id] || 0) / presupuesto.monto_base) * 100 : 0,
                          saldo_pendiente: presupuesto.monto_base - (calculos.pagosPorPresupuesto[presupuesto.id] || 0)
                        }
                      }}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onRestore={() => {}}
                      filterVigencia="activos"
                      onDetail={onDetail}
                      onAddAdicional={onAddAdicional}
                      formatearMoneda={formatearMoneda}
                      calcularEquivalencia={calcularEquivalencia}
                      getEstadoColor={getEstadoColor}
                      getEstadoLabel={getEstadoLabel}
                      isMobile={isMobile}
                      permisosCliente={permisosCliente}
                      index={index}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default Presupuestos;
